import {
  IconAlertTriangle,
  IconArchive,
  IconArrowLeft,
  IconBell,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
  IconCopy,
  IconDots,
  IconEye,
  IconFileText,
  IconInbox,
  IconLayoutKanban,
  IconLayoutSidebarRight,
  IconLock,
  IconLogout,
  IconMail,
  IconMailOpened,
  IconMenu2,
  IconMoon,
  IconPaperclip,
  IconPencil,
  IconPin,
  IconPinned,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconSettings,
  IconShieldCheck,
  IconSparkles,
  IconStar,
  IconSun,
  IconTag,
  IconTrash,
  IconUser,
  IconUsers,
  IconWifiOff,
  IconX,
} from "@tabler/icons-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { AiGenerateResponse, AiHistoryItem, AiMode, AttachmentSummary, BoardConfigResponse, BoardResponsible, InboxMessage, MessagePatchInput, MessageStage, RecipientField, RecipientValidationIssue, RecipientValidationResponse, SendMessageInput, SessionMailbox, SessionResponse } from "../shared/contracts";
import { apiRequest } from "./api";
import { AttachmentPreviewDialog } from "./AttachmentPreviewDialog";
import { EmailDocumentFrame } from "./EmailDocumentFrame";
import { KanbanBoard } from "./KanbanBoard";
import { LoginScreen } from "./LoginScreen";
import { PwaPrompt } from "./PwaPrompt";
import { SettingsPage, settingsPath, settingsRouteFromPath, type SettingsRoute } from "./SettingsPage";
import { useInbox } from "./use-inbox";
import { useMailNotifications, type BackgroundNotificationStatus, type MailNotificationPermission } from "./use-mail-notifications";
import { useSession } from "./use-session";

type Theme = "light" | "dark";
type MainView = "mail" | "board" | "search" | "settings";
type FolderFilter = "all" | "unread" | "starred" | "sent" | "drafts" | "archive" | "trash";

const folderPaths: Record<FolderFilter, string> = {
  all: "/caixa-de-entrada",
  unread: "/nao-lidos",
  starred: "/importantes",
  sent: "/enviados",
  drafts: "/rascunhos",
  archive: "/arquivo",
  trash: "/lixeira",
};

interface AppLocation {
  mainView: MainView;
  activeFolder: FolderFilter;
  settingsRoute: SettingsRoute;
  searchQuery: string;
}

interface NotificationTarget {
  tenantId: string;
  mailboxId: string;
  messageId: string;
}

const notificationTargetKeys = ["tenantId", "mailboxId", "messageId"] as const;

function notificationTargetFromUrl(search = window.location.search): NotificationTarget | null {
  const parameters = new URLSearchParams(search);
  const tenantId = parameters.get("tenantId")?.trim() || "";
  const mailboxId = parameters.get("mailboxId")?.trim() || "";
  const messageId = parameters.get("messageId")?.trim() || "";
  if (!tenantId || !mailboxId || !messageId || [tenantId, mailboxId, messageId].some((value) => value.length > 128)) {
    return null;
  }
  return { tenantId, mailboxId, messageId };
}

function normalizePathname(pathname: string): string {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "") || "/";
}

function appLocationFromUrl(pathname = window.location.pathname, search = window.location.search): AppLocation {
  const normalizedPath = normalizePathname(pathname);
  const settingsRoute = settingsRouteFromPath(normalizedPath);
  if (settingsRoute) {
    return { mainView: "settings", activeFolder: "all", settingsRoute, searchQuery: "" };
  }

  const folderEntry = (Object.entries(folderPaths) as Array<[FolderFilter, string]>)
    .find(([, path]) => path === normalizedPath);
  if (folderEntry) {
    return { mainView: "mail", activeFolder: folderEntry[0], settingsRoute: "overview", searchQuery: "" };
  }

  if (normalizedPath === "/quadro") {
    return { mainView: "board", activeFolder: "all", settingsRoute: "overview", searchQuery: "" };
  }
  if (normalizedPath === "/buscar") {
    return {
      mainView: "search",
      activeFolder: "all",
      settingsRoute: "overview",
      searchQuery: new URLSearchParams(search).get("q") ?? "",
    };
  }

  return { mainView: "mail", activeFolder: "all", settingsRoute: "overview", searchQuery: "" };
}

function searchPath(query: string): string {
  const normalizedQuery = query.trim();
  return normalizedQuery ? `/buscar?q=${encodeURIComponent(normalizedQuery)}` : "/buscar";
}

const stageLabels: Record<MessageStage, string> = {
  inbox: "Entrada",
  attention: "Em foco",
  waiting: "Aguardando",
  done: "Concluído",
};

function formatFullMessageDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "full",
    timeStyle: "short",
  }).format(date);
}

function localDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftedDateKey(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

function initialsForName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words.at(-1)?.[0] ?? ""}`.toLocaleUpperCase("pt-BR");
  return (words[0] || "PM").slice(0, 2).toLocaleUpperCase("pt-BR");
}

function formatFileSize(size: number): string {
  if (size < 1_000) return `${size} B`;
  if (size < 1_000_000) return `${Math.max(1, Math.round(size / 1_000))} KB`;
  return `${(size / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

function useOnlineStatus() {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return online;
}

function initialTheme(): Theme {
  try {
    return window.localStorage.getItem("powermail-theme") === "dark" ? "dark" : "light";
  } catch {
    // Light remains the safe default when storage is unavailable.
  }
  return "light";
}

function AppLogo({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`app-logo${compact ? " app-logo--compact" : ""}`} aria-label="PowerMail">
      <span className="app-logo__mark" aria-hidden="true">
        <img src="/brand/simbolo-powermail.jpeg" alt="" />
      </span>
      {compact ? null : <span className="app-logo__name">PowerMail</span>}
    </div>
  );
}

interface NavItemProps {
  active?: boolean;
  count?: number;
  icon: ComponentChildren;
  label: string;
  onClick: () => void;
}

function NavItem({ active = false, count, icon, label, onClick }: NavItemProps) {
  return (
    <button className={`nav-item${active ? " is-active" : ""}`} type="button" onClick={onClick}>
      <span className="nav-item__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nav-item__label">{label}</span>
      {typeof count === "number" && count > 0 ? <span className="nav-item__count">{count}</span> : null}
    </button>
  );
}

interface SidebarProps {
  activeFolder: FolderFilter;
  collapsed: boolean;
  draftCount: number;
  mainView: MainView;
  session: SessionResponse;
  unreadCount: number;
  onCompose: () => void;
  onFolderChange: (folder: FolderFilter) => void;
  onMailboxChange: (mailboxId: string) => Promise<void>;
  onRefreshMailboxes: () => void;
  onToggleCollapsed: () => void;
  onViewChange: (view: MainView) => void;
}

interface MailboxSwitcherDialogProps {
  open: boolean;
  session: SessionResponse;
  onClose: () => void;
  onSelect: (mailboxId: string) => void;
}

function MailboxSwitcherDialog({ open, session, onClose, onSelect }: MailboxSwitcherDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const activeMailbox = session.mailboxes.find((mailbox) => mailbox.id === session.activeMailboxId) ?? null;
  const [selectedDomain, setSelectedDomain] = useState("all");
  const [query, setQuery] = useState("");
  const domains = useMemo(() => {
    const grouped = new Map<string, { unread: number; mailboxes: number }>();
    for (const mailbox of session.mailboxes) {
      const current = grouped.get(mailbox.domain) ?? { unread: 0, mailboxes: 0 };
      current.unread += mailbox.unreadCount;
      current.mailboxes += 1;
      grouped.set(mailbox.domain, current);
    }
    return [...grouped.entries()].sort(([left], [right]) => left.localeCompare(right, "pt-BR"));
  }, [session.mailboxes]);
  const totalUnread = session.mailboxes.reduce((total, mailbox) => total + mailbox.unreadCount, 0);
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const visibleMailboxes = useMemo(() => session.mailboxes.filter((mailbox) => {
    if (selectedDomain !== "all" && mailbox.domain !== selectedDomain) return false;
    if (!normalizedQuery) return true;
    return `${mailbox.displayName} ${mailbox.address} ${mailbox.tenantName}`
      .toLocaleLowerCase("pt-BR")
      .includes(normalizedQuery);
  }), [normalizedQuery, selectedDomain, session.mailboxes]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setSelectedDomain(activeMailbox?.domain ?? "all");
      setQuery("");
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [activeMailbox?.domain, open]);

  return (
    <dialog
      className="mailbox-dialog"
      ref={dialogRef}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={onClose}
    >
      <header className="mailbox-dialog__header">
        <div>
          <span>Central de caixas</span>
          <strong>Escolha onde quer trabalhar</strong>
          <small>{session.account.isRoot ? "Acesso root a todos os domínios" : session.tenant.role === "member" ? "Caixa vinculada à sua conta" : "Caixas administradas por você"}</small>
        </div>
        <div>
          {totalUnread > 0 ? <span className="mailbox-dialog__total"><b>{totalUnread}</b> não lidos</span> : null}
          <button className="icon-button icon-button--quiet" type="button" onClick={onClose} aria-label="Fechar seleção de caixas"><IconX size={19} /></button>
        </div>
      </header>
      <div className="mailbox-dialog__layout">
        <nav className="mailbox-domains" aria-label="Domínios de e-mail">
          <span>Domínios</span>
          <button className={selectedDomain === "all" ? "is-active" : ""} type="button" onClick={() => setSelectedDomain("all")}>
            <span><IconMail size={16} />Todas as caixas</span>
            <b>{session.mailboxes.length}</b>
          </button>
          {domains.map(([domain, summary]) => (
            <button className={selectedDomain === domain ? "is-active" : ""} type="button" key={domain} onClick={() => setSelectedDomain(domain)}>
              <span><i>@</i>{domain}</span>
              <b>{summary.unread > 0 ? summary.unread : summary.mailboxes}</b>
            </button>
          ))}
        </nav>
        <section className="mailbox-dialog__content">
          <label className="mailbox-dialog__search">
            <IconSearch size={17} aria-hidden="true" />
            <input value={query} onInput={(event) => setQuery(event.currentTarget.value)} placeholder="Buscar nome, e-mail ou empresa" autoFocus />
          </label>
          <div className="mailbox-dialog__summary">
            <div><strong>{selectedDomain === "all" ? "Todas as caixas" : `@${selectedDomain}`}</strong><span>{visibleMailboxes.length} {visibleMailboxes.length === 1 ? "caixa disponível" : "caixas disponíveis"}</span></div>
            <small>Os não lidos são atualizados ao abrir este painel.</small>
          </div>
          <div className="mailbox-dialog__list">
            {visibleMailboxes.map((mailbox) => (
              <button
                className={mailbox.id === session.activeMailboxId ? "is-active" : ""}
                type="button"
                key={mailbox.id}
                onClick={() => {
                  if (mailbox.id !== session.activeMailboxId) onSelect(mailbox.id);
                  onClose();
                }}
              >
                <span className="mailbox-dialog__avatar">{initialsForName(mailbox.displayName || mailbox.address)}</span>
                <span className="mailbox-dialog__copy">
                  <strong>{mailbox.displayName || mailbox.address.split("@")[0]}</strong>
                  <small>{mailbox.address}</small>
                  <em>{mailbox.tenantName}</em>
                </span>
                <span className="mailbox-dialog__state">
                  {mailbox.unreadCount > 0 ? <b>{mailbox.unreadCount} não {mailbox.unreadCount === 1 ? "lido" : "lidos"}</b> : <small>Em dia</small>}
                  {mailbox.id === session.activeMailboxId ? <em><IconCheck size={14} /> Em uso</em> : <em>Abrir caixa</em>}
                </span>
              </button>
            ))}
            {visibleMailboxes.length === 0 ? <div className="mailbox-dialog__empty"><IconSearch size={22} /><strong>Nenhuma caixa encontrada</strong><span>Tente outro nome ou selecione outro domínio.</span></div> : null}
          </div>
        </section>
      </div>
    </dialog>
  );
}

interface AccountControlProps {
  session: SessionResponse;
  theme: Theme;
  onLogout: () => void;
  onMailboxChange: (mailboxId: string) => void;
  onOpenSettings: () => void;
  onRefreshMailboxes: () => void;
  onRestartWelcome: () => void;
  onThemeChange: () => void;
}

function AccountControl({ session, theme, onLogout, onMailboxChange, onOpenSettings, onRefreshMailboxes, onRestartWelcome, onThemeChange }: AccountControlProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mailboxDialogOpen, setMailboxDialogOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeMailbox = session.mailboxes.find((mailbox) => mailbox.id === session.activeMailboxId) ?? null;
  const totalUnread = session.mailboxes.reduce((total, mailbox) => total + mailbox.unreadCount, 0);

  useEffect(() => {
    if (!menuOpen) return;
    const closeMenu = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeMenu);
    return () => document.removeEventListener("pointerdown", closeMenu);
  }, [menuOpen]);

  return (
    <div className="topbar-account-wrap" ref={menuRef}>
      <button
        className="topbar-account"
        type="button"
        onClick={() => setMenuOpen((current) => {
          const next = !current;
          if (next) onRefreshMailboxes();
          return next;
        })}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        data-tour="account"
      >
        <span className="topbar-account__copy"><strong>{session.account.name}</strong><small>{activeMailbox?.address || session.tenant.name}</small></span>
        <span className="avatar avatar--small avatar--brand">{initialsForName(session.account.name)}</span>
        <IconChevronDown className={menuOpen ? "is-open" : ""} size={16} aria-hidden="true" />
      </button>
      {menuOpen ? (
        <div className="topbar-account-menu" role="menu" aria-label="Menu da conta">
          <div className="topbar-account-menu__identity">
            <span className="avatar avatar--small avatar--brand">{initialsForName(session.account.name)}</span>
            <span><strong>{session.account.name}</strong><small>{session.account.email}</small>{session.account.isRoot ? <em>ROOT</em> : null}</span>
          </div>
          <button className="topbar-account-menu__mailbox" type="button" onClick={() => { setMenuOpen(false); setMailboxDialogOpen(true); }}>
            <IconMail size={18} />
            <span><strong>Trocar caixa</strong><small>{activeMailbox?.address || "Nenhuma caixa"}</small></span>
            {totalUnread > 0 ? <b>{totalUnread}</b> : null}
            <IconChevronRight size={15} />
          </button>
          <div className="topbar-account-menu__divider" />
          <button type="button" role="menuitem" onClick={() => { onThemeChange(); setMenuOpen(false); }}>
            {theme === "light" ? <IconMoon size={17} /> : <IconSun size={17} />}
            <span><strong>{theme === "light" ? "Usar tema escuro" : "Usar tema claro"}</strong><small>Aparência neste dispositivo</small></span>
          </button>
          <button type="button" role="menuitem" onClick={() => { onOpenSettings(); setMenuOpen(false); }}>
            <IconSettings size={17} />
            <span><strong>Configurações</strong><small>Conta, caixas e domínios</small></span>
          </button>
          <button type="button" role="menuitem" onClick={() => { onRestartWelcome(); setMenuOpen(false); }}>
            <IconSparkles size={17} />
            <span><strong>Reativar boas-vindas</strong><small>Rever o guia da Bia</small></span>
          </button>
          <div className="topbar-account-menu__divider" />
          <button className="topbar-account-menu__logout" type="button" role="menuitem" onClick={onLogout}>
            <IconLogout size={17} />
            <span><strong>Sair</strong><small>Encerrar esta sessão</small></span>
          </button>
        </div>
      ) : null}
      <MailboxSwitcherDialog open={mailboxDialogOpen} session={session} onClose={() => setMailboxDialogOpen(false)} onSelect={onMailboxChange} />
    </div>
  );
}

interface SidebarMailboxSwitcherProps {
  activeUnreadCount: number;
  collapsed: boolean;
  session: SessionResponse;
  onMailboxChange: (mailboxId: string) => Promise<void>;
  onRefresh: () => void;
}

function readPinnedMailboxIds(storageKey: string): string[] {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) || "[]") as unknown;
    return Array.isArray(stored) ? stored.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function SidebarMailboxSwitcher({ activeUnreadCount, collapsed, session, onMailboxChange, onRefresh }: SidebarMailboxSwitcherProps) {
  const storageKey = `powermail:pinned-mailboxes:${session.account.id}`;
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => readPinnedMailboxIds(storageKey));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const switcherRef = useRef<HTMLElement>(null);
  const activeMailbox = session.mailboxes.find((mailbox) => mailbox.id === session.activeMailboxId) ?? null;
  const canPinMailboxes = session.account.isRoot || session.tenant.role === "owner" || session.tenant.role === "admin";
  const validMailboxIds = useMemo(() => new Set(session.mailboxes.map((mailbox) => mailbox.id)), [session.mailboxes]);
  const visibleMailboxes = useMemo(() => {
    if (!activeMailbox) return [];
    return [
      activeMailbox,
      ...session.mailboxes.filter((mailbox) => mailbox.id !== activeMailbox.id && pinnedIds.includes(mailbox.id)),
    ];
  }, [activeMailbox, pinnedIds, session.mailboxes]);

  useEffect(() => {
    setPinnedIds((current) => {
      const next = current.filter((mailboxId) => validMailboxIds.has(mailboxId));
      if (next.length === current.length) return current;
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* storage may be unavailable */ }
      return next;
    });
  }, [storageKey, validMailboxIds]);

  useEffect(() => {
    if (!settingsOpen) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setSettingsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSettingsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [settingsOpen]);

  if (!activeMailbox || !canPinMailboxes) return null;

  const togglePinned = (mailboxId: string) => {
    setPinnedIds((current) => {
      const next = current.includes(mailboxId)
        ? current.filter((candidate) => candidate !== mailboxId)
        : [...current, mailboxId];
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* storage may be unavailable */ }
      return next;
    });
  };

  const selectMailbox = async (mailboxId: string) => {
    if (mailboxId === session.activeMailboxId || switchingTo) return;
    setSwitchingTo(mailboxId);
    try {
      await onMailboxChange(mailboxId);
    } finally {
      setSwitchingTo(null);
    }
  };

  const toggleSettings = () => {
    setSettingsOpen((current) => {
      if (!current) onRefresh();
      return !current;
    });
  };

  const chooseMailbox = (mailboxId: string) => {
    if (mailboxId === session.activeMailboxId || switchingTo) return;
    setSettingsOpen(false);
    void selectMailbox(mailboxId);
  };

  return (
    <section className="sidebar-mailboxes" ref={switcherRef} aria-label="Troca rápida de caixa">
      <header className="sidebar-mailboxes__header">
        <span>Caixas</span>
        <button
          type="button"
          onClick={toggleSettings}
          aria-label="Configurar caixas fixadas"
          aria-expanded={settingsOpen}
          title="Configurar caixas fixadas"
        >
          <IconSettings size={15} aria-hidden="true" />
        </button>
      </header>

      <div className="sidebar-mailboxes__list">
        {visibleMailboxes.map((mailbox) => {
          const active = mailbox.id === session.activeMailboxId;
          const unread = active ? activeUnreadCount : mailbox.unreadCount;
          const switching = switchingTo === mailbox.id;
          return (
            <div
              className={`sidebar-mailboxes__item${active ? " is-active" : ""}${switching ? " is-switching" : ""}`}
              key={mailbox.id}
            >
              {active ? (
                <div
                  className="sidebar-mailboxes__select"
                  aria-current="page"
                  aria-label={`Caixa atual: ${mailbox.address}${unread > 0 ? `, ${unread} não lidos` : ""}`}
                  title={collapsed ? mailbox.address : undefined}
                >
                  <span className="sidebar-mailboxes__avatar">{initialsForName(mailbox.displayName || mailbox.address)}</span>
                  <span className="sidebar-mailboxes__copy">
                    <strong>{mailbox.displayName || mailbox.address.split("@")[0]}</strong>
                    <small>{mailbox.address}</small>
                  </span>
                </div>
              ) : (
                <button
                  className="sidebar-mailboxes__select"
                  type="button"
                  onClick={() => void selectMailbox(mailbox.id)}
                  disabled={Boolean(switchingTo)}
                  aria-label={`Abrir caixa: ${mailbox.address}${unread > 0 ? `, ${unread} não lidos` : ""}`}
                  title={collapsed ? mailbox.address : undefined}
                >
                  <span className="sidebar-mailboxes__avatar">{initialsForName(mailbox.displayName || mailbox.address)}</span>
                  <span className="sidebar-mailboxes__copy">
                    <strong>{mailbox.displayName || mailbox.address.split("@")[0]}</strong>
                    <small>{mailbox.address}</small>
                  </span>
                </button>
              )}
              <span className="sidebar-mailboxes__meta">
                {switching ? <IconRefresh className="is-spinning" size={15} aria-hidden="true" /> : null}
                {unread > 0 ? <b>{unread > 99 ? "99+" : unread}</b> : null}
                {active ? (
                  <button
                    className="sidebar-mailboxes__expand"
                    type="button"
                    onClick={toggleSettings}
                    aria-label="Abrir caixas fixadas"
                    aria-expanded={settingsOpen}
                    title="Abrir caixas fixadas"
                  >
                    <IconChevronDown size={16} aria-hidden="true" />
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>

      {settingsOpen ? (
        <div className="sidebar-mailboxes__settings" role="dialog" aria-label="Configurar caixas fixadas">
          <div>
            <strong>Caixas fixadas</strong>
            <small>{session.account.isRoot ? "Escolha caixas de qualquer domínio." : session.tenant.role === "admin" ? `Somente @${activeMailbox.domain}.` : "Escolha as caixas que ficam sempre visíveis."}</small>
          </div>
          <div className="sidebar-mailboxes__options">
            {session.mailboxes.map((mailbox) => {
              const active = mailbox.id === session.activeMailboxId;
              const pinned = pinnedIds.includes(mailbox.id);
              return (
                <div
                  key={mailbox.id}
                  className={`sidebar-mailboxes__option${active ? " is-active" : ""}${pinned ? " is-pinned" : ""}`}
                >
                  <button
                    className="sidebar-mailboxes__option-select"
                    type="button"
                    onClick={() => chooseMailbox(mailbox.id)}
                    disabled={active || Boolean(switchingTo)}
                    aria-current={active ? "page" : undefined}
                    aria-label={active ? `Caixa atual: ${mailbox.address}` : `Abrir caixa: ${mailbox.address}`}
                  >
                    <span><strong>{mailbox.displayName || mailbox.address.split("@")[0]}</strong><small>{mailbox.address}</small></span>
                  </button>
                  {active ? <em>Atual</em> : null}
                  <button
                    className="sidebar-mailboxes__pin"
                    type="button"
                    onClick={() => togglePinned(mailbox.id)}
                    aria-label={`${pinned ? "Desafixar" : "Fixar"} ${mailbox.address}`}
                    aria-pressed={pinned}
                    title={pinned ? "Desafixar caixa" : "Fixar caixa"}
                  >
                    {pinned ? <IconPinned size={16} aria-hidden="true" /> : <IconPin size={16} aria-hidden="true" />}
                  </button>
                </div>
              );
            })}
          </div>
          <p>Clique no nome para abrir. Use o pin para manter a caixa na lateral.</p>
        </div>
      ) : null}
    </section>
  );
}

function Sidebar({
  activeFolder,
  collapsed,
  draftCount,
  mainView,
  session,
  unreadCount,
  onCompose,
  onFolderChange,
  onMailboxChange,
  onRefreshMailboxes,
  onToggleCollapsed,
  onViewChange,
}: SidebarProps) {
  const openFolder = (folder: FolderFilter) => {
    onFolderChange(folder);
  };

  return (
    <aside className={`sidebar${collapsed ? " sidebar--collapsed" : ""}`}>
      <div className="sidebar__top">
        <AppLogo />
        <button
          className="icon-button icon-button--quiet sidebar__collapse"
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          <IconMenu2 size={18} aria-hidden="true" />
        </button>
      </div>

      <button className="compose-button" type="button" onClick={onCompose} data-tour="compose">
        <IconPencil size={18} aria-hidden="true" />
        <span>Escrever</span>
      </button>

      <SidebarMailboxSwitcher
        activeUnreadCount={unreadCount}
        collapsed={collapsed}
        session={session}
        onMailboxChange={onMailboxChange}
        onRefresh={onRefreshMailboxes}
      />

      <nav className="sidebar__nav" aria-label="Navegação principal" data-tour="navigation">
        <div className="nav-group">
          <span className="nav-group__label">Caixa de entrada</span>
          <NavItem
            active={mainView === "mail" && activeFolder === "all"}
            count={unreadCount}
            icon={<IconInbox size={18} />}
            label="Todos"
            onClick={() => openFolder("all")}
          />
          <NavItem
            active={mainView === "mail" && activeFolder === "unread"}
            icon={<IconMailOpened size={18} />}
            label="Não lidos"
            onClick={() => openFolder("unread")}
          />
          <NavItem
            active={mainView === "mail" && activeFolder === "starred"}
            icon={<IconStar size={18} />}
            label="Importantes"
            onClick={() => openFolder("starred")}
          />
        </div>

        <div className="nav-group">
          <span className="nav-group__label">Trabalho</span>
          <NavItem
            active={mainView === "board"}
            icon={<IconLayoutKanban size={18} />}
            label="Quadro"
            onClick={() => onViewChange("board")}
          />
          <NavItem
            active={mainView === "mail" && activeFolder === "sent"}
            icon={<IconSend size={18} />}
            label="Enviados"
            onClick={() => openFolder("sent")}
          />
          <NavItem
            active={mainView === "mail" && activeFolder === "drafts"}
            count={draftCount}
            icon={<IconFileText size={18} />}
            label="Rascunhos"
            onClick={() => openFolder("drafts")}
          />
        </div>

        <div className="nav-group nav-group--secondary">
          <NavItem
            active={mainView === "mail" && activeFolder === "archive"}
            icon={<IconArchive size={18} />}
            label="Arquivo"
            onClick={() => openFolder("archive")}
          />
          <NavItem
            active={mainView === "mail" && activeFolder === "trash"}
            icon={<IconTrash size={18} />}
            label="Lixeira"
            onClick={() => openFolder("trash")}
          />
        </div>
      </nav>

    </aside>
  );
}

function messageSearchText(message: InboxMessage): string {
  return `${message.sender.name} ${message.sender.email} ${message.fromAddress} ${message.toAddress} ${message.subject} ${message.preview} ${message.body.join(" ")}`
    .toLocaleLowerCase("pt-BR");
}

function messageLocationLabel(message: InboxMessage): string {
  if (message.folder === "trash") return "Lixeira";
  if (message.folder === "archive") return "Arquivo";
  if (message.deliveryStatus === "draft") return "Rascunho";
  return message.direction === "outbound" ? "Enviado" : "Entrada";
}

interface TopbarProps {
  messages: InboxMessage[];
  online: boolean;
  session: SessionResponse;
  syncing: boolean;
  theme: Theme;
  onLogout: () => void;
  onMailboxChange: (mailboxId: string) => void;
  onOpenMessage: (id: string) => void;
  onOpenSettings: () => void;
  onRefreshMailboxes: () => void;
  onRestartWelcome: () => void;
  onShowAllResults: (query: string) => void;
  onThemeChange: () => void;
}

function Topbar({ messages, online, session, syncing, theme, onLogout, onMailboxChange, onOpenMessage, onOpenSettings, onRefreshMailboxes, onRestartWelcome, onShowAllResults, onThemeChange }: TopbarProps) {
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const results = useMemo(
    () =>
      (normalizedQuery ? messages.filter((message) => messageSearchText(message).includes(normalizedQuery)) : messages)
        .slice(0, 5),
    [messages, normalizedQuery],
  );

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase("pt-BR") === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setSearchOpen(true);
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    const closeSearch = (event: PointerEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) setSearchOpen(false);
    };
    document.addEventListener("keydown", handleShortcut);
    document.addEventListener("pointerdown", closeSearch);
    return () => {
      document.removeEventListener("keydown", handleShortcut);
      document.removeEventListener("pointerdown", closeSearch);
    };
  }, []);

  return (
    <header className="topbar">
      <div className="topbar__mobile-brand">
        <AppLogo compact />
      </div>
      <div className="global-search-wrap" ref={searchRef} data-tour="search">
        <label className="global-search">
          <IconSearch size={18} aria-hidden="true" />
          <span className="sr-only">Pesquisar mensagens</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            placeholder="Pesquisar em tudo"
            aria-label="Pesquisar em tudo"
            aria-expanded={searchOpen}
            onFocus={() => setSearchOpen(true)}
            onInput={(event) => {
              setQuery(event.currentTarget.value);
              setSearchOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                setSearchOpen(false);
                onShowAllResults(query);
              }
            }}
          />
          <kbd>⌘ K</kbd>
        </label>
        {searchOpen ? (
          <div className="global-search-results" role="dialog" aria-label="Resultados da pesquisa">
            <div className="global-search-results__header">
              <strong>{normalizedQuery ? "Resultados" : "Mensagens recentes"}</strong>
              <span>{normalizedQuery ? `${results.length}${messages.length > results.length ? "+" : ""}` : ""}</span>
            </div>
            <div className="global-search-results__list">
              {results.length > 0 ? results.map((message) => (
                <button
                  type="button"
                  key={message.id}
                  onClick={() => {
                    setSearchOpen(false);
                    onOpenMessage(message.id);
                  }}
                >
                  <span className={`avatar avatar--tiny avatar--${message.sender.color}`}>{message.sender.initials}</span>
                  <span>
                    <strong>{message.subject}</strong>
                    <small>{message.sender.name} · {message.preview}</small>
                  </span>
                  <em>{messageLocationLabel(message)}</em>
                </button>
              )) : (
                <div className="global-search-results__empty">Nenhuma mensagem encontrada.</div>
              )}
            </div>
            <button
              className="global-search-results__all"
              type="button"
              onClick={() => {
                setSearchOpen(false);
                onShowAllResults(query);
              }}
            >
              <IconSearch size={16} aria-hidden="true" />
              Ver todos os resultados
            </button>
          </div>
        ) : null}
      </div>
      <div className="topbar__actions">
        <span className={`connection-status${online ? "" : " is-offline"}${syncing ? " is-syncing" : ""}`}>
          {syncing ? <IconRefresh size={14} aria-hidden="true" /> : online ? <IconCheck size={14} aria-hidden="true" /> : <IconWifiOff size={14} aria-hidden="true" />}
          {syncing ? "Sincronizando…" : online ? "Sincronizado" : "Sem conexão"}
        </span>
        <AccountControl
          session={session}
          theme={theme}
          onLogout={onLogout}
          onMailboxChange={onMailboxChange}
          onOpenSettings={onOpenSettings}
          onRefreshMailboxes={onRefreshMailboxes}
          onRestartWelcome={onRestartWelcome}
          onThemeChange={onThemeChange}
        />
      </div>
    </header>
  );
}

function InboxSkeleton() {
  return (
    <div className="message-skeletons" aria-label="Carregando mensagens">
      {Array.from({ length: 5 }, (_, index) => (
        <div className="message-skeleton" key={index}>
          <span className="skeleton skeleton--avatar" />
          <div>
            <span className="skeleton skeleton--line skeleton--short" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--line skeleton--medium" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface MessageRowProps {
  active: boolean;
  message: InboxMessage;
  onOpen: () => void;
  onToggleStar: () => void;
}

function MessageRow({ active, message, onOpen, onToggleStar }: MessageRowProps) {
  return (
    <article className={`message-row${active ? " is-active" : ""}${message.unread ? " is-unread" : ""}`}>
      <button className="message-row__main" type="button" onClick={onOpen} aria-label={`Abrir ${message.subject}`}>
        <span className={`avatar avatar--${message.sender.color}`}>{message.sender.initials}</span>
        <span className="message-row__content">
          <span className="message-row__meta">
            <strong>{message.sender.name}</strong>
            <time dateTime={message.receivedAt}>{message.timestampLabel}</time>
          </span>
          <span className="message-row__subject">{message.subject}</span>
          <span className="message-row__preview">{message.preview}</span>
          <span className="message-row__footer">
            {message.labels.slice(0, 2).map((label) => (
              <span className="label" key={label}>
                {label}
              </span>
            ))}
            {message.hasAttachment ? <IconPaperclip size={14} aria-label="Possui anexo" /> : null}
          </span>
        </span>
      </button>
      <button
        className={`message-row__star${message.starred ? " is-starred" : ""}`}
        type="button"
        onClick={onToggleStar}
        aria-label={message.starred ? "Remover dos importantes" : "Marcar como importante"}
      >
        <IconStar size={17} fill={message.starred ? "currentColor" : "none"} aria-hidden="true" />
      </button>
    </article>
  );
}

interface MessageListProps {
  activeFolder: FolderFilter;
  messages: InboxMessage[];
  selectedId: string | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  syncedAt: string | null;
  onOpen: (id: string) => void;
  onRetry: () => void;
  onSearch: (value: string) => void;
  onToggleStar: (id: string) => void;
}

function MessageList({
  activeFolder,
  messages,
  selectedId,
  status,
  error,
  syncedAt,
  onOpen,
  onRetry,
  onSearch,
  onToggleStar,
}: MessageListProps) {
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest" | "unread">("newest");
  const folderTitle: Record<FolderFilter, string> = {
    all: "Caixa de entrada",
    unread: "Não lidos",
    starred: "Importantes",
    sent: "Enviados",
    drafts: "Rascunhos",
    archive: "Arquivo",
    trash: "Lixeira",
  };

  const syncLabel = syncedAt
    ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(syncedAt))
    : null;
  const sortedMessages = useMemo(
    () => [...messages].sort((left, right) => {
      if (sortOrder === "unread" && left.unread !== right.unread) return left.unread ? -1 : 1;
      const comparison = new Date(right.receivedAt).getTime() - new Date(left.receivedAt).getTime();
      return sortOrder === "oldest" ? -comparison : comparison;
    }),
    [messages, sortOrder],
  );

  return (
    <section className="message-list" aria-label={folderTitle[activeFolder]}>
      <header className="message-list__header">
        <div>
          <h1>{folderTitle[activeFolder]}</h1>
          <p>{syncLabel ? `Atualizada às ${syncLabel}` : "Mensagens da equipe"}</p>
        </div>
        <button className="icon-button" type="button" onClick={onRetry} aria-label="Atualizar mensagens">
          <IconRefresh size={18} aria-hidden="true" />
        </button>
      </header>
      <label className="list-search">
        <IconSearch size={17} aria-hidden="true" />
        <span className="sr-only">Filtrar mensagens</span>
        <input type="search" placeholder="Filtrar mensagens" onInput={(event) => onSearch(event.currentTarget.value)} />
      </label>

      <div className="message-list__toolbar">
        <span>{messages.length} conversas</span>
        <label className="message-sort">
          <span className="sr-only">Ordenar mensagens</span>
          <select value={sortOrder} onChange={(event) => setSortOrder(event.currentTarget.value as typeof sortOrder)}>
            <option value="newest">Mais recentes</option>
            <option value="oldest">Mais antigos</option>
            <option value="unread">Não lidos primeiro</option>
          </select>
          <IconChevronDown size={15} aria-hidden="true" />
        </label>
      </div>

      <div className="message-list__scroll">
        {status === "idle" || status === "loading" ? <InboxSkeleton /> : null}
        {status === "error" ? (
          <div className="inline-state" role="alert">
            <IconWifiOff size={24} aria-hidden="true" />
            <strong>Não foi possível carregar</strong>
            <p>{error}</p>
            <button className="button button--secondary button--small" type="button" onClick={onRetry}>
              Tentar novamente
            </button>
          </div>
        ) : null}
        {status === "ready" && messages.length === 0 ? (
          <div className="inline-state">
            <IconMailOpened size={26} aria-hidden="true" />
            <strong>Nada por aqui</strong>
            <p>Altere o filtro ou faça uma nova busca.</p>
          </div>
        ) : null}
        {status === "ready"
          ? sortedMessages.map((message) => (
              <MessageRow
                active={message.id === selectedId}
                key={message.id}
                message={message}
                onOpen={() => onOpen(message.id)}
                onToggleStar={() => onToggleStar(message.id)}
              />
            ))
          : null}
      </div>
    </section>
  );
}

interface MessageReaderProps {
  message: InboxMessage | null;
  onArchive: () => void;
  onBack: () => void;
  onCompose: () => void;
  onOpenFull?: () => void;
  onToggleWorkflow: () => void;
  onToggleRead: () => void;
  onToggleStar: () => void;
  onTrash: () => void;
  workflowOpen: boolean;
}

function MessageReader({
  message,
  onArchive,
  onBack,
  onCompose,
  onOpenFull,
  onToggleWorkflow,
  onToggleRead,
  onToggleStar,
  onTrash,
  workflowOpen,
}: MessageReaderProps) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentSummary | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActionsOpen(false);
    setDetailsOpen(false);
    setPreviewAttachment(null);
  }, [message?.id]);

  useEffect(() => {
    if (!actionsOpen) return;
    const closeMenu = (event: PointerEvent) => {
      if (!actionsRef.current?.contains(event.target as Node)) setActionsOpen(false);
    };
    document.addEventListener("pointerdown", closeMenu);
    return () => document.removeEventListener("pointerdown", closeMenu);
  }, [actionsOpen]);

  useEffect(() => {
    if (!detailsOpen) return;
    const closeDetails = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) setDetailsOpen(false);
    };
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDetailsOpen(false);
    };
    document.addEventListener("pointerdown", closeDetails);
    window.addEventListener("keydown", closeWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeDetails);
      window.removeEventListener("keydown", closeWithKeyboard);
    };
  }, [detailsOpen]);

  if (!message) {
    return (
      <section className="message-reader message-reader--empty">
        <div className="empty-reader__icon">
          <IconMailOpened size={30} aria-hidden="true" />
        </div>
        <h2>Escolha uma conversa</h2>
        <p>Leia, responda e transforme e-mails em ações sem trocar de tela.</p>
      </section>
    );
  }

  return (
    <section className="message-reader" aria-label={`Mensagem: ${message.subject}`}>
      <header className="reader-toolbar">
        <button className="icon-button reader-toolbar__back" type="button" onClick={onBack} aria-label="Voltar para mensagens">
          <IconArrowLeft size={19} aria-hidden="true" />
        </button>
        <div className="reader-toolbar__actions">
          {!workflowOpen ? (
            <button className="icon-button" type="button" onClick={onToggleWorkflow} aria-label="Abrir fluxo de trabalho" title="Abrir fluxo de trabalho">
              <IconLayoutSidebarRight size={18} aria-hidden="true" />
            </button>
          ) : null}
          {message.direction === "inbound" ? (
            <button className="icon-button" type="button" onClick={onToggleRead} aria-label={message.unread ? "Marcar como lida" : "Marcar como não lida"} title={message.unread ? "Marcar como lida" : "Marcar como não lida"}>
              {message.unread ? <IconMailOpened size={18} aria-hidden="true" /> : <IconMail size={18} aria-hidden="true" />}
            </button>
          ) : null}
          <button className={`icon-button${message.starred ? " is-starred" : ""}`} type="button" onClick={onToggleStar} aria-label="Alternar importância">
            <IconStar size={18} fill={message.starred ? "currentColor" : "none"} aria-hidden="true" />
          </button>
          <button className="icon-button" type="button" onClick={onArchive} aria-label={message.folder === "archive" ? "Desarquivar mensagem" : "Arquivar mensagem"} title={message.folder === "archive" ? "Desarquivar" : "Arquivar"}>
            {message.folder === "archive" ? <IconInbox size={18} aria-hidden="true" /> : <IconArchive size={18} aria-hidden="true" />}
          </button>
          <div className="reader-more" ref={actionsRef}>
            <button className="icon-button" type="button" aria-label="Mais opções" aria-expanded={actionsOpen} onClick={() => setActionsOpen((current) => !current)}>
              <IconDots size={19} aria-hidden="true" />
            </button>
            {actionsOpen ? (
              <div className="reader-more__menu" role="menu">
                {onOpenFull ? <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onOpenFull(); }}>Abrir em tela completa</button> : null}
                {message.direction === "inbound" ? <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onToggleRead(); }}>{message.unread ? "Marcar como lida" : "Marcar como não lida"}</button> : null}
                <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onToggleStar(); }}>{message.starred ? "Remover dos importantes" : "Marcar como importante"}</button>
                <button type="button" role="menuitem" onClick={() => { setActionsOpen(false); onArchive(); }}>{message.folder === "archive" ? "Desarquivar" : "Arquivar"}</button>
                <button className="is-danger" type="button" role="menuitem" onClick={() => { setActionsOpen(false); onTrash(); }}>Mover para a lixeira</button>
              </div>
            ) : null}
          </div>
          <button className="icon-button" type="button" onClick={onBack} aria-label="Fechar mensagem" title="Fechar mensagem">
            <IconX size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="reader-scroll">
        <header className="reader-heading">
          <div className="reader-heading__labels">
            {message.labels.map((label) => (
              <span className="label" key={label}>
                {label}
              </span>
            ))}
          </div>
          <h2>{message.subject}</h2>
        </header>

        <article className="email-card">
          <header className="email-card__header">
            <span className={`avatar avatar--${message.sender.color}`}>{message.sender.initials}</span>
            <div className="email-card__sender" ref={detailsRef}>
              <strong>{message.sender.name}</strong>
              <button
                className="email-card__details-trigger"
                type="button"
                aria-expanded={detailsOpen}
                aria-haspopup="dialog"
                onClick={() => setDetailsOpen((current) => !current)}
              >
                {message.direction === "inbound" ? `para ${message.toAddress}` : `de ${message.fromAddress}`}
                <IconChevronDown className={detailsOpen ? "is-open" : ""} size={14} aria-hidden="true" />
              </button>
              {detailsOpen ? (
                <div className="email-card__details" role="dialog" aria-label="Detalhes completos da mensagem">
                  <dl>
                    <div><dt>De</dt><dd><strong>{message.sender.name}</strong><span>{message.fromAddress}</span></dd></div>
                    <div><dt>Para</dt><dd>{message.toAddress}</dd></div>
                    <div><dt>Data</dt><dd>{formatFullMessageDate(message.receivedAt)}</dd></div>
                    <div><dt>Assunto</dt><dd>{message.subject}</dd></div>
                    <div><dt>Tipo</dt><dd>{message.direction === "inbound" ? "Mensagem recebida" : "Mensagem enviada"}</dd></div>
                    <div><dt>Anexos</dt><dd>{message.attachments.length ? `${message.attachments.length} arquivo${message.attachments.length === 1 ? "" : "s"}` : "Nenhum"}</dd></div>
                  </dl>
                </div>
              ) : null}
            </div>
            <time dateTime={message.receivedAt}>{message.timestampLabel}</time>
          </header>

          <div className="email-card__body">
            <EmailDocumentFrame
              key={message.id}
              src={`/api/v1/messages/${encodeURIComponent(message.id)}/body`}
              title={`Conteúdo de ${message.subject}`}
              fallbackParagraphs={message.body}
            />
          </div>

          {message.attachments.map((attachment) => (
            <button className="attachment" type="button" onClick={() => setPreviewAttachment(attachment)} key={attachment.id}>
              <span className="attachment__icon">
                <IconFileText size={20} aria-hidden="true" />
              </span>
              <span>
                <strong>{attachment.filename}</strong>
                <small>{formatFileSize(attachment.sizeBytes)}</small>
              </span>
              <IconEye size={17} aria-hidden="true" />
            </button>
          ))}
        </article>

        <div className="quick-reply">
          <button className="quick-reply__input" type="button" onClick={onCompose}>
            Responder para {message.sender.name.split(" ")[0]}
          </button>
          <button className="button button--primary" type="button" onClick={onCompose}>
            Responder
          </button>
        </div>
      </div>
      <AttachmentPreviewDialog
        item={previewAttachment ? {
          key: previewAttachment.id,
          filename: previewAttachment.filename,
          contentType: previewAttachment.contentType,
          sizeBytes: previewAttachment.sizeBytes,
          url: `/api/v1/attachments/${encodeURIComponent(previewAttachment.id)}`,
        } : null}
        onClose={() => setPreviewAttachment(null)}
      />
    </section>
  );
}

function WorkflowAiSummary({ message }: { message: InboxMessage }) {
  const [summary, setSummary] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setSummary(null); setError(null); setBusy(false); }, [message.id]);

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const response = await apiRequest<AiGenerateResponse>("/api/v1/ai/generate", {
        method: "POST",
        body: JSON.stringify({ mode: "summarize", prompt: "Resuma esta conversa.", subject: message.subject, messageContext: message.body.join("\n\n") }),
      });
      setSummary(response.text);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível gerar o resumo.");
    } finally { setBusy(false); }
  };

  return (
    <section className="workflow-section workflow-section--ai">
      <div className="ai-heading"><IconSparkles size={17} aria-hidden="true" /><h3>Resumo inteligente</h3></div>
      {summary ? <p>{summary}</p> : <p className="ai-empty">A Bia identifica decisões, pendências e a próxima ação desta conversa.</p>}
      {error ? <span className="ai-error" role="alert">{error}</span> : null}
      <button className="ai-summary-button" type="button" onClick={() => void generate()} disabled={busy}>{busy ? "Bia está analisando…" : summary ? "Gerar novamente" : "Gerar resumo com a Bia"}</button>
      <span className="ai-disclaimer">Revise informações importantes antes de agir.</span>
    </section>
  );
}

interface WorkflowPanelProps {
  message: InboxMessage | null;
  onClose: () => void;
  onPatchMessage: (patch: MessagePatchInput, successMessage?: string) => Promise<void>;
}

type WorkflowModal = "responsible" | "deadline" | "tags" | "participants";

function WorkflowDialog({
  children,
  labelledBy,
  onClose,
}: {
  children: ComponentChildren;
  labelledBy: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeWithKeyboard);
    return () => window.removeEventListener("keydown", closeWithKeyboard);
  }, [onClose]);

  return (
    <div className="board-dialog-backdrop" onMouseDown={(event) => {
      if (event.currentTarget === event.target) onClose();
    }}>
      <section className="board-dialog workflow-dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {children}
      </section>
    </div>
  );
}

function workflowResponsibleInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}`.toLocaleUpperCase("pt-BR");
  return (parts[0] || "PM").slice(0, 2).toLocaleUpperCase("pt-BR");
}

function WorkflowResponsibleAvatar({ responsible }: { responsible: BoardResponsible }) {
  return responsible.avatarUrl ? (
    <img className="board-person-avatar board-person-avatar--large" src={responsible.avatarUrl} alt="" />
  ) : (
    <span className="board-person-avatar board-person-avatar--large" aria-hidden="true">
      {workflowResponsibleInitials(responsible.name)}
    </span>
  );
}

function WorkflowPanel({ message, onClose, onPatchMessage }: WorkflowPanelProps) {
  const [modal, setModal] = useState<WorkflowModal | null>(null);
  const [config, setConfig] = useState<BoardConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [responsibleQuery, setResponsibleQuery] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  useEffect(() => {
    setModal(null);
    setError(null);
    setResponsibleQuery("");
    setTagQuery("");
  }, [message?.id]);

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setError(null);
  };

  const loadConfig = async () => {
    setConfigLoading(true);
    setError(null);
    try {
      setConfig(await apiRequest<BoardConfigResponse>("/api/v1/board/config"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível carregar as opções do quadro.");
    } finally {
      setConfigLoading(false);
    }
  };

  const openModal = (nextModal: WorkflowModal) => {
    if (!message) return;
    setError(null);
    if (nextModal === "responsible") {
      setResponsibleQuery("");
      void loadConfig();
    }
    if (nextModal === "deadline") setDueAt(message.dueAt?.slice(0, 10) || "");
    if (nextModal === "tags") {
      setTagQuery("");
      setSelectedTagIds(message.tags.map((tag) => tag.id));
      void loadConfig();
    }
    setModal(nextModal);
  };

  const applyPatch = async (patch: MessagePatchInput, successMessage: string, closeAfter = true) => {
    setBusy("message");
    setError(null);
    try {
      await onPatchMessage(patch, successMessage);
      if (closeAfter) setModal(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível atualizar a conversa.");
    } finally {
      setBusy(null);
    }
  };

  const createAndAssignResponsible = async () => {
    const name = responsibleQuery.replace(/\s+/g, " ").trim();
    if (!name) return;
    setBusy("responsible");
    setError(null);
    try {
      const responsible = await apiRequest<BoardResponsible>("/api/v1/board/responsibles", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      setConfig((current) => current ? {
        ...current,
        responsibles: [...current.responsibles.filter((item) => item.id !== responsible.id), responsible]
          .sort((left, right) => left.name.localeCompare(right.name, "pt-BR")),
      } : current);
      await onPatchMessage({ responsibleId: responsible.id }, `${responsible.name} agora é responsável.`);
      setModal(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível criar o responsável.");
    } finally {
      setBusy(null);
    }
  };

  const createTag = async () => {
    const name = tagQuery.replace(/\s+/g, " ").trim();
    if (!name) return;
    setBusy("tag");
    setError(null);
    try {
      const tag = await apiRequest<BoardConfigResponse["tags"][number]>("/api/v1/board/tags", {
        method: "POST",
        body: JSON.stringify({ name, color: "gray" }),
      });
      setConfig((current) => current ? {
        ...current,
        tags: [...current.tags.filter((item) => item.id !== tag.id), tag]
          .sort((left, right) => left.name.localeCompare(right.name, "pt-BR")),
      } : current);
      setSelectedTagIds((current) => [...new Set([...current, tag.id])]);
      setTagQuery("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível criar a tag.");
    } finally {
      setBusy(null);
    }
  };

  if (!message) return <aside className="workflow-panel workflow-panel--empty" aria-hidden="true" />;

  const normalizedResponsibleQuery = responsibleQuery.trim().toLocaleLowerCase("pt-BR");
  const filteredResponsibles = (config?.responsibles || []).filter((responsible) =>
    responsible.name.toLocaleLowerCase("pt-BR").includes(normalizedResponsibleQuery),
  );
  const canCreateResponsible = Boolean(responsibleQuery.trim())
    && !(config?.responsibles || []).some((responsible) =>
      responsible.name.localeCompare(responsibleQuery.trim(), "pt-BR", { sensitivity: "accent" }) === 0,
    );
  const normalizedTagQuery = tagQuery.trim().toLocaleLowerCase("pt-BR");
  const filteredTags = (config?.tags || []).filter((tag) => tag.name.toLocaleLowerCase("pt-BR").includes(normalizedTagQuery));
  const canCreateTag = Boolean(tagQuery.trim())
    && !(config?.tags || []).some((tag) => tag.name.localeCompare(tagQuery.trim(), "pt-BR", { sensitivity: "accent" }) === 0);

  return (
    <>
      <aside className="workflow-panel" aria-label="Organização da conversa">
        <header className="workflow-panel__header">
          <div>
            <span>Fluxo de trabalho</span>
            <strong>{stageLabels[message.stage]}</strong>
          </div>
          <button className="icon-button icon-button--quiet" type="button" onClick={onClose} aria-label="Fechar painel">
            <IconX size={18} aria-hidden="true" />
          </button>
        </header>

        <section className="workflow-section">
          <h3>Etapa</h3>
          <div className="stage-control">
            {(Object.keys(stageLabels) as MessageStage[]).map((stage) => (
              <button
                className={message.stage === stage ? "is-active" : ""}
                disabled={Boolean(busy)}
                key={stage}
                type="button"
                onClick={() => void applyPatch({ stage }, `Conversa movida para ${stageLabels[stage]}.`, false)}
                aria-pressed={message.stage === stage}
              >
                {stageLabels[stage]}
              </button>
            ))}
          </div>
        </section>

        <section className="workflow-section">
          <h3>Responsável</h3>
          <button className="property-row" type="button" onClick={() => openModal("responsible")}>
            <IconUser size={17} aria-hidden="true" />
            <span>{message.responsible?.name || message.owner || "Definir responsável"}</span>
            <IconChevronDown size={15} aria-hidden="true" />
          </button>
        </section>

        <section className="workflow-section">
          <div className="workflow-section__title">
            <h3>Próxima ação</h3>
            <button type="button" onClick={() => openModal("deadline")}>{message.task ? "Editar" : "Definir"}</button>
          </div>
          {message.task ? (
            <button className="task-card task-card--button" type="button" onClick={() => openModal("deadline")}>
              <span className="task-card__check">
                <IconCheck size={15} aria-hidden="true" />
              </span>
              <span className="task-card__content">
                <strong>{message.task.title}</strong>
                <span>
                  <IconClock size={14} aria-hidden="true" />
                  {message.task.dueLabel}
                </span>
              </span>
            </button>
          ) : (
            <button className="add-task" type="button" onClick={() => openModal("deadline")}>
              Adicionar próxima ação
            </button>
          )}
        </section>

        <section className="workflow-section">
          <h3>Tags</h3>
          <button className="property-row" type="button" onClick={() => openModal("tags")}>
            <IconTag size={17} aria-hidden="true" />
            <span>{message.tags.length ? message.tags.map((tag) => tag.name).join(", ") : "Adicionar tags"}</span>
            <IconChevronDown size={15} aria-hidden="true" />
          </button>
        </section>

        <WorkflowAiSummary message={message} />

        <section className="workflow-section">
          <h3>Participantes</h3>
          <div className="participants">
            <span className={`avatar avatar--small avatar--${message.sender.color}`}>{message.sender.initials}</span>
            <span className="avatar avatar--small avatar--brand">PM</span>
            <button type="button" aria-label="Ver participantes" onClick={() => openModal("participants")}>
              <IconUsers size={16} aria-hidden="true" />
            </button>
          </div>
        </section>
      </aside>

      {modal === "responsible" ? (
        <WorkflowDialog labelledBy="workflow-responsible-title" onClose={closeModal}>
          <header className="board-dialog__header">
            <div><span>Equipe do quadro</span><h2 id="workflow-responsible-title">Definir responsável</h2><p>Escolha alguém já cadastrado ou crie um novo responsável para esta conversa.</p></div>
            <button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button>
          </header>
          <div className="board-dialog__search"><IconSearch size={17} /><input autoFocus type="search" value={responsibleQuery} onInput={(event) => setResponsibleQuery(event.currentTarget.value)} placeholder="Buscar ou criar responsável" /></div>
          {error ? <p className="workflow-dialog__error" role="alert">{error}</p> : null}
          <div className="board-option-list">
            <button className={!message.responsible ? "is-selected" : ""} type="button" disabled={Boolean(busy)} onClick={() => void applyPatch({ responsibleId: null }, "Conversa ficou sem responsável.")}>
              <span className="board-person-avatar board-person-avatar--large"><IconUser size={17} /></span><span><strong>Sem responsável</strong><small>Deixar disponível para a equipe</small></span>{!message.responsible ? <IconCheck size={17} /> : null}
            </button>
            {filteredResponsibles.map((responsible) => (
              <button className={message.responsible?.id === responsible.id ? "is-selected" : ""} type="button" disabled={Boolean(busy)} key={responsible.id} onClick={() => void applyPatch({ responsibleId: responsible.id }, `${responsible.name} agora é responsável.`)}>
                <WorkflowResponsibleAvatar responsible={responsible} /><span><strong>{responsible.name}</strong><small>Responsável do quadro</small></span>{message.responsible?.id === responsible.id ? <IconCheck size={17} /> : null}
              </button>
            ))}
            {canCreateResponsible ? (
              <button className="board-option-list__create" type="button" disabled={Boolean(busy)} onClick={() => void createAndAssignResponsible()}>
                <span><IconPlus size={17} /></span><span><strong>Criar “{responsibleQuery.trim()}”</strong><small>Cadastrar e definir nesta conversa</small></span>
              </button>
            ) : null}
            {configLoading ? <p className="board-dialog__empty">Carregando responsáveis…</p> : null}
            {!configLoading && config && filteredResponsibles.length === 0 && !canCreateResponsible ? <p className="board-dialog__empty">Nenhum responsável encontrado.</p> : null}
          </div>
        </WorkflowDialog>
      ) : null}

      {modal === "deadline" ? (
        <WorkflowDialog labelledBy="workflow-deadline-title" onClose={closeModal}>
          <header className="board-dialog__header">
            <div><span>Próxima ação</span><h2 id="workflow-deadline-title">Definir prazo</h2><p>Escolha a data e acompanhe a urgência da conversa no quadro.</p></div>
            <button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button>
          </header>
          <div className="board-deadline-editor">
            <label className="board-field"><span>Data do prazo</span><input autoFocus type="date" value={dueAt} min="2000-01-01" max="2100-12-31" onInput={(event) => setDueAt(event.currentTarget.value)} /></label>
            <div className="board-deadline-editor__quick" aria-label="Prazos rápidos"><button type="button" onClick={() => setDueAt(localDateKey())}>Hoje</button><button type="button" onClick={() => setDueAt(shiftedDateKey(1))}>Amanhã</button><button type="button" onClick={() => setDueAt(shiftedDateKey(3))}>Em 3 dias</button><button type="button" onClick={() => setDueAt(shiftedDateKey(7))}>Em 1 semana</button></div>
            <div className="board-deadline-legend"><span className="is-safe">No prazo</span><span className="is-warning">Ontem ou hoje</span><span className="is-late">Vencido</span><span className="is-done">Concluído</span></div>
            {error ? <p className="workflow-dialog__error" role="alert">{error}</p> : null}
          </div>
          <footer className="board-dialog__footer board-dialog__footer--spread">
            <button className="button button--secondary" type="button" disabled={Boolean(busy) || !message.dueAt} onClick={() => void applyPatch({ dueAt: null }, "Prazo removido.")}>Remover prazo</button>
            <div><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void applyPatch({ stage: message.stage === "done" ? "inbox" : "done" }, message.stage === "done" ? "Conversa reaberta." : "Conversa concluída.")}><IconCircleCheck size={16} /> {message.stage === "done" ? "Reabrir" : "Concluir"}</button><button className="button button--primary" type="button" disabled={Boolean(busy) || !dueAt} onClick={() => void applyPatch({ dueAt }, "Prazo atualizado.")}>Salvar prazo</button></div>
          </footer>
        </WorkflowDialog>
      ) : null}

      {modal === "tags" ? (
        <WorkflowDialog labelledBy="workflow-tags-title" onClose={closeModal}>
          <header className="board-dialog__header">
            <div><span>Organização visual</span><h2 id="workflow-tags-title">Tags da conversa</h2><p>Selecione as tags ou crie uma nova para organizar o trabalho.</p></div>
            <button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button>
          </header>
          <div className="board-dialog__search"><IconSearch size={17} /><input autoFocus type="search" value={tagQuery} onInput={(event) => setTagQuery(event.currentTarget.value)} placeholder="Buscar ou criar tag" /></div>
          {error ? <p className="workflow-dialog__error" role="alert">{error}</p> : null}
          <div className="board-tag-manager">
            {filteredTags.map((tag) => {
              const selected = selectedTagIds.includes(tag.id);
              return <div className="board-tag-option" key={tag.id}><button className={selected ? "is-selected" : ""} type="button" onClick={() => setSelectedTagIds((current) => selected ? current.filter((id) => id !== tag.id) : [...current, tag.id])}><span className={`board-tag board-tag--${tag.color}`}>{tag.name}</span>{selected ? <IconCheck size={17} /> : null}</button></div>;
            })}
            {canCreateTag ? <button className="workflow-create-tag" type="button" disabled={Boolean(busy)} onClick={() => void createTag()}><IconPlus size={16} /> Criar tag “{tagQuery.trim()}”</button> : null}
            {configLoading ? <p className="board-dialog__empty">Carregando tags…</p> : null}
            {!configLoading && config && filteredTags.length === 0 && !canCreateTag ? <p className="board-dialog__empty">Nenhuma tag encontrada.</p> : null}
          </div>
          <footer className="board-dialog__footer"><button className="button button--secondary" type="button" onClick={closeModal}>Cancelar</button><button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void applyPatch({ tagIds: selectedTagIds }, "Tags atualizadas.")}>Salvar seleção</button></footer>
        </WorkflowDialog>
      ) : null}

      {modal === "participants" ? (
        <WorkflowDialog labelledBy="workflow-participants-title" onClose={closeModal}>
          <header className="board-dialog__header">
            <div><span>Envelope da mensagem</span><h2 id="workflow-participants-title">Participantes</h2><p>Os participantes vêm dos endereços originais do e-mail e não alteram usuários ou responsáveis.</p></div>
            <button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button>
          </header>
          <dl className="workflow-participant-list">
            <div><dt>Remetente</dt><dd><strong>{message.sender.name}</strong><span>{message.fromAddress}</span></dd></div>
            <div><dt>Destinatário</dt><dd><strong>{message.toAddress}</strong><span>{message.direction === "inbound" ? "Caixa que recebeu a mensagem" : "Destinatário da mensagem enviada"}</span></dd></div>
          </dl>
          <footer className="board-dialog__footer"><button className="button button--primary" type="button" onClick={closeModal}>Fechar</button></footer>
        </WorkflowDialog>
      ) : null}
    </>
  );
}

interface MessageFullDialogProps {
  message: InboxMessage | null;
  onArchive: () => void;
  onClose: () => void;
  onCompose: () => void;
  onPatchMessage: (patch: MessagePatchInput, successMessage?: string) => Promise<void>;
  onToggleRead: () => void;
  onToggleStar: () => void;
  onTrash: () => void;
}

function MessageFullDialog({
  message,
  onArchive,
  onClose,
  onCompose,
  onPatchMessage,
  onToggleRead,
  onToggleStar,
  onTrash,
}: MessageFullDialogProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const [workflowOpen, setWorkflowOpen] = useState(() => window.matchMedia("(min-width: 840px)").matches);

  useEffect(() => {
    if (!message) return;
    setWorkflowOpen(window.matchMedia("(min-width: 840px)").matches);
  }, [message?.id]);

  useEffect(() => {
    if (!message) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector(".board-dialog-backdrop")) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    dialogRef.current?.focus();
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className="message-full-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        className={`message-full-dialog${workflowOpen ? "" : " is-workflow-closed"}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`E-mail: ${message.subject}`}
        tabIndex={-1}
      >
        <MessageReader
          message={message}
          onArchive={onArchive}
          onBack={onClose}
          onCompose={onCompose}
          onToggleWorkflow={() => setWorkflowOpen((current) => !current)}
          onToggleRead={onToggleRead}
          onToggleStar={onToggleStar}
          onTrash={onTrash}
          workflowOpen={workflowOpen}
        />
        {workflowOpen ? (
          <WorkflowPanel
            message={message}
            onClose={() => setWorkflowOpen(false)}
            onPatchMessage={onPatchMessage}
          />
        ) : null}
      </section>
    </div>
  );
}

function SearchPage({
  initialQuery,
  messages,
  onOpenMessage,
}: {
  initialQuery: string;
  messages: InboxMessage[];
  onOpenMessage: (id: string) => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  useEffect(() => setQuery(initialQuery), [initialQuery]);
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const results = useMemo(
    () => messages.filter((message) => !normalizedQuery || messageSearchText(message).includes(normalizedQuery)),
    [messages, normalizedQuery],
  );

  return (
    <section className="search-page">
      <header className="page-heading">
        <span>Busca global</span>
        <h1>Encontre qualquer conversa</h1>
        <p>Pesquise por pessoa, endereço, assunto ou conteúdo da mensagem.</p>
      </header>
      <label className="search-page__field">
        <IconSearch size={20} aria-hidden="true" />
        <input autoFocus type="search" value={query} placeholder="Digite para pesquisar…" onInput={(event) => setQuery(event.currentTarget.value)} />
      </label>
      <div className="search-page__summary">{results.length} {results.length === 1 ? "conversa encontrada" : "conversas encontradas"}</div>
      <div className="search-page__results">
        {results.map((message) => (
          <button type="button" key={message.id} onClick={() => onOpenMessage(message.id)}>
            <span className={`avatar avatar--${message.sender.color}`}>{message.sender.initials}</span>
            <span className="search-page__copy">
              <span><strong>{message.subject}</strong><em>{messageLocationLabel(message)}</em></span>
              <small>{message.sender.name} · {message.sender.email}</small>
              <p>{message.preview}</p>
            </span>
            <time dateTime={message.receivedAt}>{message.timestampLabel}</time>
          </button>
        ))}
        {results.length === 0 ? (
          <div className="inline-state"><IconSearch size={26} aria-hidden="true" /><strong>Nenhum resultado</strong><p>Tente outro nome, endereço ou assunto.</p></div>
        ) : null}
      </div>
    </section>
  );
}

function escapeEditorText(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function serializeEditorNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeEditorText(node.textContent || "");
  if (!(node instanceof HTMLElement)) return "";
  const children = Array.from(node.childNodes, serializeEditorNode).join("");
  const tag = node.tagName.toLocaleLowerCase("en-US");
  if (tag === "br") return "<br>";
  if (tag === "strong" || tag === "b") return `<strong>${children}</strong>`;
  if (tag === "em" || tag === "i") return `<em>${children}</em>`;
  if (tag === "u") return `<u>${children}</u>`;
  if (tag === "ul" || tag === "ol" || tag === "li") return `<${tag}>${children}</${tag}>`;
  if (tag === "a") {
    const href = node.getAttribute("href") || "";
    if (/^(https?:\/\/|mailto:)/i.test(href)) return `<a href="${escapeEditorText(href).replaceAll('"', "&quot;")}">${children}</a>`;
    return children;
  }
  if (tag === "div" || tag === "p") return `<p>${children || "<br>"}</p>`;
  return children;
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length)));
  }
  return btoa(binary);
}

interface ComposeDialogProps {
  mailboxes: SessionMailbox[];
  messages: InboxMessage[];
  hiddenRecipientEmails: string[];
  open: boolean;
  replyTo: InboxMessage | null;
  onClose: () => void;
  onHideRecipientSuggestion: (email: string) => Promise<void>;
  onSend: (input: SendMessageInput) => Promise<void>;
}

function ComposeDialog({ mailboxes, messages, hiddenRecipientEmails, open, replyTo, onClose, onHideRecipientSuggestion, onSend }: ComposeDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const aiAnimationRef = useRef<{ frameId: number | null; resolve: () => void } | null>(null);
  const mailbox = mailboxes[0] || null;
  const [recipient, setRecipient] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [copyFieldsOpen, setCopyFieldsOpen] = useState(false);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewAttachment, setPreviewAttachment] = useState<File | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPhase, setAiPhase] = useState<"idle" | "thinking" | "typing">("idle");
  const [validatingRecipients, setValidatingRecipients] = useState(false);
  const [recipientValidation, setRecipientValidation] = useState<RecipientValidationResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contacts = useMemo(() => {
    const unique = new Map<string, { email: string; name: string }>();
    const hidden = new Set(hiddenRecipientEmails.map((email) => email.toLocaleLowerCase("en-US")));
    for (const message of messages) {
      const candidates = message.direction === "inbound"
        ? [{ email: message.sender.email, name: message.sender.name }]
        : message.toAddress.split(",").map((email) => ({ email: email.trim(), name: email.trim() }));
      for (const contact of candidates) {
        const key = contact.email.toLocaleLowerCase("en-US");
        if (key && key.includes("@") && !hidden.has(key) && key !== mailbox?.address.toLocaleLowerCase("en-US")) unique.set(key, contact);
      }
    }
    return [...unique.values()].sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
  }, [hiddenRecipientEmails, mailbox?.address, messages]);
  const recipientFragment = recipient.split(/[;,]/).at(-1)?.trim().toLocaleLowerCase("pt-BR") || "";
  const recipientSuggestions = contacts.filter((contact) => !recipientFragment || `${contact.name} ${contact.email}`.toLocaleLowerCase("pt-BR").includes(recipientFragment)).slice(0, 6);

  const setEditorText = (value: string) => {
    if (editorRef.current) editorRef.current.innerText = value;
    setBody(value);
  };

  const stopAiAnimation = () => {
    const animation = aiAnimationRef.current;
    if (!animation) return;
    if (animation.frameId !== null) cancelAnimationFrame(animation.frameId);
    aiAnimationRef.current = null;
    animation.resolve();
  };

  const animateText = (value: string, onFrame: (partial: string) => void) => {
    stopAiAnimation();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || value.length < 2) {
      onFrame(value);
      return Promise.resolve();
    }
    const characters = Array.from(value);
    const duration = Math.min(5_200, Math.max(900, characters.length * 9));
    return new Promise<void>((resolve) => {
      let startedAt: number | null = null;
      let visibleCharacters = -1;
      const finish = () => {
        onFrame(value);
        aiAnimationRef.current = null;
        resolve();
      };
      const frame = (timestamp: number) => {
        startedAt ??= timestamp;
        const progress = Math.min(1, (timestamp - startedAt) / duration);
        const nextVisibleCharacters = Math.max(1, Math.floor(characters.length * progress));
        if (nextVisibleCharacters !== visibleCharacters) {
          visibleCharacters = nextVisibleCharacters;
          onFrame(characters.slice(0, visibleCharacters).join(""));
        }
        if (progress >= 1) {
          finish();
          return;
        }
        const frameId = requestAnimationFrame(frame);
        if (aiAnimationRef.current) aiAnimationRef.current.frameId = frameId;
      };
      aiAnimationRef.current = { frameId: requestAnimationFrame(frame), resolve };
    });
  };

  const animateEditorText = async (value: string) => {
    setBody("");
    if (editorRef.current) editorRef.current.textContent = "";
    await animateText(value, (partial) => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.textContent = partial;
      editor.scrollTop = editor.scrollHeight;
    });
    setBody(value.trim());
    editorRef.current?.focus();
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    stopAiAnimation();
    const replyAddress = replyTo?.direction === "inbound" ? replyTo.sender.email : replyTo?.toAddress.split(",")[0]?.trim();
    setRecipient(replyAddress || "");
    setCc(""); setBcc(""); setCopyFieldsOpen(false); setRecipientOpen(false);
    setSubject(replyTo ? (replyTo.subject.toLocaleLowerCase("pt-BR").startsWith("re:") ? replyTo.subject : `Re: ${replyTo.subject}`) : "");
    setEditorText("");
    setAttachments([]); setPreviewAttachment(null); setAiInstruction(""); setAiOpen(false); setAiBusy(false); setAiPhase("idle"); setValidatingRecipients(false); setRecipientValidation(null); setSending(false); setError(null);
  }, [open, replyTo]);

  useEffect(() => () => stopAiAnimation(), []);

  const parseAddresses = (value: string) => value.split(/[;,]/).map((entry) => entry.trim()).filter(Boolean);
  const insertContact = (email: string) => {
    const parts = recipient.split(/[;,]/);
    parts[parts.length - 1] = email;
    setRecipient(`${parts.map((part) => part.trim()).filter(Boolean).join(", ")}, `);
    setRecipientOpen(false);
    setRecipientValidation(null);
  };
  const hideContact = async (email: string) => {
    setError(null);
    try {
      await onHideRecipientSuggestion(email);
      setRecipientOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível remover essa sugestão.");
    }
  };
  const syncEditor = () => setBody(editorRef.current?.innerText.trim() || "");
  const format = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditor();
  };
  const addLink = () => {
    const url = window.prompt("Cole o endereço do link (https://…)");
    if (url && /^https?:\/\//i.test(url)) format("createLink", url);
  };

  const runAi = async (mode: AiMode) => {
    if (mode === "improve" && !body) { setError("Escreva um rascunho antes de pedir uma melhoria."); return; }
    if (mode === "compose" && !aiInstruction.trim()) { setError("Conte para a Bia o que o e-mail precisa dizer."); return; }
    stopAiAnimation(); setAiBusy(true); setAiPhase("thinking"); setError(null);
    try {
      const response = await apiRequest<AiGenerateResponse>("/api/v1/ai/generate", {
        method: "POST",
        body: JSON.stringify({
          mode,
          prompt: aiInstruction.trim() || (mode === "subject" ? "Crie o melhor assunto para este texto." : "Melhore este e-mail."),
          subject,
          body,
          ...(replyTo ? { messageContext: `${replyTo.subject}\n\n${replyTo.body.join("\n\n")}` } : {}),
        }),
      });
      setAiPhase("typing");
      if (mode === "subject") {
        const nextSubject = response.text.replace(/^Assunto:\s*/i, "").replace(/^['\"]|['\"]$/g, "");
        setSubject("");
        await animateText(nextSubject, setSubject);
      } else {
        await animateEditorText(response.text);
      }
      setAiInstruction("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "A Bia não conseguiu ajudar agora.");
    } finally { setAiPhase("idle"); setAiBusy(false); }
  };

  const addAttachments = (files: FileList | null) => {
    if (!files?.length) return;
    const next = [...attachments, ...Array.from(files)].slice(0, 5);
    if (attachments.length + files.length > 5) { setError("Envie no máximo 5 anexos por mensagem."); return; }
    if (next.some((file) => file.size === 0) || next.reduce((sum, file) => sum + file.size, 0) > 6 * 1024 * 1024) {
      setError("Os anexos devem somar no máximo 6 MB e não podem estar vazios.");
      return;
    }
    setAttachments(next); setError(null);
  };

  const deliver = async () => {
    const text = editorRef.current?.innerText.trim() || "";
    if (!text) { setError("Escreva a mensagem antes de enviar."); return; }
    setSending(true); setError(null);
    try {
      const encodedAttachments = await Promise.all(attachments.map(async (file) => ({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        contentBase64: await fileToBase64(file),
      })));
      await onSend({
        mailboxId: mailbox?.id || "",
        to: parseAddresses(recipient),
        ...(cc.trim() ? { cc: parseAddresses(cc) } : {}),
        ...(bcc.trim() ? { bcc: parseAddresses(bcc) } : {}),
        subject,
        text,
        html: Array.from(editorRef.current?.childNodes || [], serializeEditorNode).join(""),
        ...(encodedAttachments.length ? { attachments: encodedAttachments } : {}),
        ...(replyTo ? { replyToMessageId: replyTo.id } : {}),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível enviar a mensagem.");
    } finally { setSending(false); }
  };

  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    const text = editorRef.current?.innerText.trim() || "";
    if (!text) { setError("Escreva a mensagem antes de enviar."); return; }
    setValidatingRecipients(true); setRecipientValidation(null); setError(null);
    try {
      const validation = await apiRequest<RecipientValidationResponse>("/api/v1/recipient-validation", {
        method: "POST",
        body: JSON.stringify({
          to: parseAddresses(recipient),
          ...(cc.trim() ? { cc: parseAddresses(cc) } : {}),
          ...(bcc.trim() ? { bcc: parseAddresses(bcc) } : {}),
        }),
      });
      if (validation.issues.length > 0) {
        setRecipientValidation(validation);
        return;
      }
      await deliver();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível verificar os destinatários.");
    } finally {
      setValidatingRecipients(false);
    }
  };

  const replaceRecipient = (issue: RecipientValidationIssue) => {
    if (!issue.suggestedEmail) return;
    const replaceInList = (value: string) => value
      .split(/[;,]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => entry.toLocaleLowerCase("en-US") === issue.email.toLocaleLowerCase("en-US") ? issue.suggestedEmail! : entry)
      .join(", ");
    const setters: Record<RecipientField, (value: string) => void> = { to: setRecipient, cc: setCc, bcc: setBcc };
    const values: Record<RecipientField, string> = { to: recipient, cc, bcc };
    setters[issue.field](replaceInList(values[issue.field]));
    setRecipientValidation(null);
    setRecipientOpen(false);
  };

  return (
    <>
    <dialog className="compose-dialog" ref={dialogRef} onCancel={(event) => { event.preventDefault(); if (!sending && !aiBusy && !validatingRecipients) onClose(); }} onClose={onClose}>
      <form onSubmit={(event) => void submit(event)}>
        <header className="compose-dialog__header">
          <div><span>Nova mensagem</span><strong>{replyTo ? "Responder conversa" : "Escrever e-mail"}</strong></div>
          <button className="icon-button icon-button--quiet" type="button" onClick={onClose} aria-label="Fechar mensagem" disabled={sending || aiBusy || validatingRecipients}><IconX size={18} aria-hidden="true" /></button>
        </header>
        <div className="compose-dialog__fields">
          <div className="compose-field compose-field--from"><span>De</span><div><strong>{mailbox?.displayName || "Caixa indisponível"}</strong><small>{mailbox?.address || "Sua conta ainda não possui uma caixa própria."}</small></div><IconLock size={15} aria-label="Remetente vinculado à conta" /></div>
          <div className="compose-field compose-field--recipient">
            <span>Para</span>
            <div className="recipient-autocomplete">
              <input type="text" inputMode="email" value={recipient} onFocus={() => setRecipientOpen(true)} onBlur={() => window.setTimeout(() => setRecipientOpen(false), 120)} onInput={(event) => { setRecipient(event.currentTarget.value); setRecipientOpen(true); setRecipientValidation(null); }} placeholder="nome@empresa.com" autoComplete="off" disabled={sending || validatingRecipients} required />
              {recipientOpen && recipientSuggestions.length > 0 ? <div className="recipient-suggestions">{recipientSuggestions.map((contact) => <div className="recipient-suggestion" key={contact.email}><button className="recipient-suggestion__select" type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => insertContact(contact.email)}><span className="avatar avatar--tiny">{initialsForName(contact.name)}</span><span><strong>{contact.name}</strong><small>{contact.email}</small></span></button><button className="recipient-suggestion__remove" type="button" title="Remover das sugestões" aria-label={`Remover ${contact.email} das sugestões`} onMouseDown={(event) => event.preventDefault()} onClick={() => void hideContact(contact.email)}><IconTrash size={16} aria-hidden="true" /></button></div>)}</div> : null}
            </div>
            <button className="copy-fields-toggle" type="button" onClick={() => setCopyFieldsOpen((current) => !current)}>Cc/Cco</button>
          </div>
          {copyFieldsOpen ? <><label><span>Cc</span><input type="text" inputMode="email" value={cc} onInput={(event) => { setCc(event.currentTarget.value); setRecipientValidation(null); }} placeholder="Cópia" disabled={sending || validatingRecipients} /></label><label><span>Cco</span><input type="text" inputMode="email" value={bcc} onInput={(event) => { setBcc(event.currentTarget.value); setRecipientValidation(null); }} placeholder="Cópia oculta" disabled={sending || validatingRecipients} /></label></> : null}
          {recipientValidation?.issues.length ? <section className={`recipient-review${recipientValidation.canSend ? "" : " is-blocking"}`} role="alert" aria-live="assertive"><header><span>{recipientValidation.canSend ? <IconAlertTriangle size={19} aria-hidden="true" /> : <IconShieldCheck size={19} aria-hidden="true" />}</span><div><strong>{recipientValidation.canSend ? "Confira antes de enviar" : "Corrija os destinatários"}</strong><small>{recipientValidation.canSend ? "Encontramos um endereço que pode ter sido digitado errado." : "Um ou mais endereços não podem receber esta mensagem."}</small></div><button type="button" onClick={() => setRecipientValidation(null)} aria-label="Fechar verificação"><IconX size={16} aria-hidden="true" /></button></header><ul>{recipientValidation.issues.map((issue) => <li className={issue.severity === "error" ? "is-error" : ""} key={`${issue.field}-${issue.email}-${issue.code}`}><div><span>{issue.field === "to" ? "Para" : issue.field === "cc" ? "Cc" : "Cco"}</span><strong>{issue.email}</strong><small>{issue.message}</small></div>{issue.suggestedEmail ? <button type="button" onClick={() => replaceRecipient(issue)}>Usar {issue.suggestedEmail}</button> : null}</li>)}</ul><footer><small>A validação confirma o domínio, mas não consegue garantir que a caixa individual exista.</small><div><button className="button button--quiet" type="button" onClick={() => setRecipientValidation(null)}>Revisar manualmente</button>{recipientValidation.canSend ? <button className="button button--primary" type="button" onClick={() => { setRecipientValidation(null); void deliver(); }}>Enviar assim mesmo</button> : null}</div></footer></section> : null}
          <label><span>Assunto</span><input type="text" value={subject} onInput={(event) => setSubject(event.currentTarget.value)} placeholder="Sobre o que vamos conversar?" maxLength={998} disabled={aiBusy} required /></label>
          <section className={`compose-ai${aiOpen ? " is-open" : ""}${aiBusy ? " is-busy" : ""}`} aria-label="Assistente de escrita Bia">
            <button className="compose-ai__toggle" type="button" onClick={() => setAiOpen((current) => !current)} aria-expanded={aiOpen} disabled={aiBusy}>
              <span className="compose-ai__mark"><IconSparkles size={15} aria-hidden="true" /></span>
              <span className="compose-ai__copy"><strong>Escrever com a Bia</strong><small>{aiBusy ? aiPhase === "typing" ? "Colocando as palavras no lugar" : "Pensando no melhor texto" : "Crie, revise ou encontre o melhor assunto"}</small></span>
              {aiBusy ? <span className="compose-ai__activity" aria-label={aiPhase === "typing" ? "Bia está escrevendo" : "Bia está pensando"}><i /></span> : <IconChevronDown className="compose-ai__chevron" size={16} aria-hidden="true" />}
            </button>
            {aiOpen ? <div className="compose-ai__panel">
              <div className="compose-ai__prompt">
                <IconSparkles size={15} aria-hidden="true" />
                <textarea rows={2} maxLength={8_000} value={aiInstruction} onInput={(event) => setAiInstruction(event.currentTarget.value)} placeholder={replyTo ? "Diga como você quer responder…" : body ? "Diga como quer melhorar este texto…" : "Conte em poucas palavras o e-mail que você precisa…"} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.isComposing) { event.preventDefault(); void runAi(body ? "improve" : replyTo ? "reply" : "compose"); } }} disabled={aiBusy} />
                <button type="button" onClick={() => void runAi(body ? "improve" : replyTo ? "reply" : "compose")} disabled={aiBusy || (!body && !aiInstruction.trim())} aria-label={replyTo ? "Criar resposta com a Bia" : body ? "Melhorar texto com a Bia" : "Criar e-mail com a Bia"}><IconSend size={15} aria-hidden="true" /></button>
              </div>
              <div className="compose-ai__quick-actions"><span>Ações rápidas</span><div><button type="button" disabled={aiBusy || !body} onClick={() => void runAi("improve")}>Revisar texto</button><button type="button" disabled={aiBusy || !body} onClick={() => void runAi("subject")}>Criar assunto</button>{replyTo ? <button type="button" disabled={aiBusy} onClick={() => void runAi("reply")}>Sugerir resposta</button> : null}</div></div>
            </div> : null}
          </section>
          <div className="compose-editor-wrap">
            <div className="compose-formatbar" role="toolbar" aria-label="Formatação da mensagem">
              <button type="button" onClick={() => format("bold")} aria-label="Negrito" disabled={aiBusy}><strong>B</strong></button>
              <button type="button" onClick={() => format("italic")} aria-label="Itálico" disabled={aiBusy}><em>I</em></button>
              <button type="button" onClick={() => format("underline")} aria-label="Sublinhado" disabled={aiBusy}><u>U</u></button>
              <i />
              <button type="button" onClick={() => format("insertUnorderedList")} aria-label="Lista com marcadores" disabled={aiBusy}>• Lista</button>
              <button type="button" onClick={() => format("insertOrderedList")} aria-label="Lista numerada" disabled={aiBusy}>1. Lista</button>
              <button type="button" onClick={addLink} aria-label="Inserir link" disabled={aiBusy}>Link</button>
              <button type="button" onClick={() => format("removeFormat")} aria-label="Limpar formatação" disabled={aiBusy}>Limpar</button>
            </div>
            <div className="compose-editor-stage"><div ref={editorRef} className={`compose-editor${aiPhase === "thinking" ? " is-ai-thinking" : aiPhase === "typing" ? " is-ai-typing" : ""}`} contentEditable={!aiBusy} role="textbox" aria-multiline="true" aria-busy={aiBusy} data-placeholder="Escreva sua mensagem…" onInput={syncEditor} onPaste={(event) => { event.preventDefault(); format("insertText", event.clipboardData?.getData("text/plain") || ""); }} />{aiBusy ? <div className={`compose-ai-writing-status is-${aiPhase}`} role="status" aria-live="polite"><i /><span>{aiPhase === "typing" ? "Bia está escrevendo" : "Bia está pensando"}</span></div> : null}</div>
          </div>
          {attachments.length ? <div className="compose-attachments" aria-label="Anexos selecionados">{attachments.map((file, index) => <div className="compose-attachment" key={`${file.name}-${file.size}-${file.lastModified}-${index}`}><button className="compose-attachment__preview" type="button" onClick={() => setPreviewAttachment(file)} aria-label={`Visualizar ${file.name}`}><IconPaperclip size={14} aria-hidden="true" /><span><strong>{file.name}</strong><small>{formatFileSize(file.size)}</small></span></button><button className="compose-attachment__remove" type="button" onClick={() => { setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index)); setPreviewAttachment((current) => current === file ? null : current); }} aria-label={`Remover ${file.name}`}><IconX size={14} aria-hidden="true" /></button></div>)}</div> : null}
          {error ? <p className="compose-dialog__error" role="alert">{error}</p> : null}
        </div>
        <footer className="compose-dialog__footer">
          <div><input ref={attachmentInputRef} type="file" multiple hidden onChange={(event) => { addAttachments(event.currentTarget.files); event.currentTarget.value = ""; }} /><button className="icon-button" type="button" onClick={() => attachmentInputRef.current?.click()} aria-label="Adicionar anexos" title="Adicionar anexos (até 6 MB)" disabled={sending || validatingRecipients || attachments.length >= 5}><IconPaperclip size={18} aria-hidden="true" /></button><span><IconShieldCheck size={13} aria-hidden="true" /> Domínios verificados antes do envio · até 50 destinatários</span></div>
          <button className="button button--primary" type="submit" disabled={sending || validatingRecipients || aiBusy || !mailbox}>{validatingRecipients ? "Verificando…" : sending ? "Enviando…" : "Enviar agora"}</button>
        </footer>
      </form>
    </dialog>
    <AttachmentPreviewDialog
      item={previewAttachment ? {
        key: `${previewAttachment.name}:${previewAttachment.size}:${previewAttachment.lastModified}`,
        filename: previewAttachment.name,
        contentType: previewAttachment.type || "application/octet-stream",
        sizeBytes: previewAttachment.size,
        file: previewAttachment,
      } : null}
      onClose={() => setPreviewAttachment(null)}
    />
    </>
  );
}

const welcomeSteps = [
  { selector: '[data-tour="compose"]', title: "Escreva com velocidade", description: "Crie uma mensagem completa, use formatação e peça para a Bia redigir ou melhorar o texto." },
  { selector: '[data-tour="search"]', title: "Encontre qualquer conversa", description: "Pesquise pessoas, endereços, assuntos e conteúdo sem sair do seu fluxo de trabalho." },
  { selector: '[data-tour="navigation"]', title: "Tudo organizado", description: "Entrada, importantes, enviados, arquivo e o quadro Kanban ficam sempre a um clique." },
  { selector: '[data-tour="account"]', title: "Sua conta e preferências", description: "Abra este menu para ver o perfil, alterar a senha, sair ou reativar estas boas-vindas." },
] as const;

const EDGE_NOTIFICATION_SETTINGS = "edge://settings/content/notifications";

function NotificationSetupModal({
  isEdge,
  open,
  permission,
  status,
  onActivate,
  onClose,
}: {
  isEdge: boolean;
  open: boolean;
  permission: MailNotificationPermission;
  status: BackgroundNotificationStatus;
  onActivate: () => Promise<BackgroundNotificationStatus>;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const manual = status === "manual" || permission === "denied";
  const busy = status === "subscribing";
  const failed = status === "error";

  const copyEdgeSettings = async () => {
    try {
      await navigator.clipboard.writeText(EDGE_NOTIFICATION_SETTINGS);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="notification-setup" role="dialog" aria-modal="true" aria-labelledby="notification-setup-title">
      <button className="notification-setup__veil" type="button" onClick={onClose} aria-label="Agora não" />
      <section className="notification-setup__card">
        <header>
          <span className="notification-setup__identity">
            <img src="/brand/bia-avatar.png" alt="" />
            <span><strong>Bia</strong><small>Assistente do PowerMail</small></span>
          </span>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar"><IconX size={19} /></button>
        </header>

        {manual ? (
          <div className="notification-setup__body notification-setup__body--manual">
            <span className="notification-setup__icon"><IconBell size={24} /></span>
            <span className="notification-setup__eyebrow">PERMISSÃO DO NAVEGADOR</span>
            <h2 id="notification-setup-title">{isEdge ? "Vamos liberar no Microsoft Edge" : "Libere as notificações do PowerMail"}</h2>
            <p>{isEdge ? "O Edge não permite repetir a pergunta automática quando a permissão foi bloqueada. Faça assim — leva poucos segundos:" : "A permissão foi bloqueada pelo navegador. Libere-a manualmente para receber novos e-mails."}</p>
            <ol>
              <li><b>1</b><span>Clique no ícone de controles ou cadeado à esquerda do endereço.</span></li>
              <li><b>2</b><span>Abra <strong>Permissões para este site</strong>.</span></li>
              <li><b>3</b><span>Em <strong>Notificações</strong>, escolha <strong>Permitir</strong>.</span></li>
            </ol>
            {isEdge ? (
              <div className="notification-setup__edge-link">
                <span><small>Alternativa nas configurações do Edge</small><code>{EDGE_NOTIFICATION_SETTINGS}</code></span>
                <button type="button" onClick={() => void copyEdgeSettings()}><IconCopy size={16} /> {copied ? "Copiado" : "Copiar"}</button>
              </div>
            ) : null}
            <p className="notification-setup__hint">Se ainda não aparecer, confira também as notificações do {isEdge ? "Microsoft Edge nas configurações do Windows" : "navegador no sistema operacional"}.</p>
          </div>
        ) : (
          <div className="notification-setup__body">
            <span className="notification-setup__icon"><IconBell size={24} /></span>
            <span className="notification-setup__eyebrow">NOVOS E-MAILS</span>
            <h2 id="notification-setup-title">{failed ? "Não consegui concluir a ativação" : "Posso avisar mesmo com o PowerMail fechado"}</h2>
            <p>{failed ? "A ativação não terminou desta vez. Tente novamente; se o navegador bloquear, eu mostro o caminho manual." : "Ative as notificações para a Bia avisar assim que um novo e-mail chegar, mesmo sem uma aba aberta."}</p>
            <div className="notification-setup__benefits">
              <span><IconCheck size={16} /> Aviso em segundo plano</span>
              <span><IconCheck size={16} /> Clique para abrir o PowerMail</span>
              <span><IconShieldCheck size={16} /> Você pode desativar quando quiser</span>
            </div>
          </div>
        )}

        <footer>
          <button type="button" onClick={onClose}>Agora não</button>
          <button className="button button--primary" type="button" onClick={() => void onActivate()} disabled={busy}>
            {busy ? <><IconRefresh className="is-spinning" size={17} /> Ativando…</> : manual || failed ? "Tentar novamente" : "Ativar notificações"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function BiaAssistant({
  notificationSetupOpen,
  notificationStatus,
  notificationPermission,
  onEnableNotifications,
  selectedMessage,
  session,
  onCompose,
}: {
  notificationSetupOpen: boolean;
  notificationStatus: BackgroundNotificationStatus;
  notificationPermission: MailNotificationPermission;
  onEnableNotifications: () => void;
  selectedMessage: InboxMessage | null;
  session: SessionResponse;
  onCompose: () => void;
}) {
  const storageKey = `powermail:bia:welcome:${session.account.id}:${session.tenant.id}`;
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [notificationNudgeDismissed, setNotificationNudgeDismissed] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [tourStep, setTourStep] = useState<number | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tourPosition, setTourPosition] = useState({ left: 24, top: 120 });
  const [history, setHistory] = useState<AiHistoryItem[]>([
    { role: "model", text: `Olá, ${session.account.name.split(" ")[0]}! Sou a Bia. Posso ajudar a escrever e-mails e organizar seu PowerMail.` },
  ]);
  const logRef = useRef<HTMLDivElement>(null);
  const tourCardRef = useRef<HTMLElement>(null);
  const showNotificationNudge = !open
    && !nudge
    && !introOpen
    && !notificationSetupOpen
    && tourStep === null
    && !notificationNudgeDismissed
    && notificationStatus !== "enabled"
    && notificationStatus !== "unsupported";

  const finishTour = () => {
    localStorage.setItem(storageKey, "1");
    setIntroOpen(false);
    setTourStep(null);
    setNudge(true);
    window.setTimeout(() => setNudge(false), 7000);
  };
  const startTour = () => {
    setOpen(false);
    setNudge(false);
    setTourStep(null);
    setIntroOpen(true);
  };

  useEffect(() => {
    const restart = () => {
      localStorage.removeItem(storageKey);
      startTour();
    };
    window.addEventListener("powermail:restart-welcome", restart);
    if (notificationSetupOpen) {
      return () => window.removeEventListener("powermail:restart-welcome", restart);
    }
    const timer = window.setTimeout(() => {
      if (localStorage.getItem(storageKey) !== "1") startTour();
      else setNudge(true);
    }, 900);
    const nudgeTimer = window.setTimeout(() => setNudge(false), 9000);
    return () => {
      window.removeEventListener("powermail:restart-welcome", restart);
      window.clearTimeout(timer);
      window.clearTimeout(nudgeTimer);
    };
  }, [notificationSetupOpen, storageKey]);

  useEffect(() => {
    if (tourStep === null) return;
    const target = document.querySelector<HTMLElement>(welcomeSteps[tourStep].selector);
    target?.classList.add("powermail-tour-target");
    target?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const position = () => {
      if (!target) return;
      const targetRect = target.getBoundingClientRect();
      const cardRect = tourCardRef.current?.getBoundingClientRect();
      const cardWidth = cardRect?.width || 390;
      const cardHeight = cardRect?.height || 300;
      const gap = 18;
      const margin = 16;
      let left: number;
      let top: number;
      if (targetRect.right + gap + cardWidth <= window.innerWidth - margin) {
        left = targetRect.right + gap;
        top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
      } else if (targetRect.left - gap - cardWidth >= margin) {
        left = targetRect.left - gap - cardWidth;
        top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
      } else {
        left = targetRect.left + targetRect.width / 2 - cardWidth / 2;
        top = targetRect.bottom + gap + cardHeight <= window.innerHeight - margin
          ? targetRect.bottom + gap
          : targetRect.top - gap - cardHeight;
      }
      setTourPosition({
        left: Math.max(margin, Math.min(left, window.innerWidth - cardWidth - margin)),
        top: Math.max(margin, Math.min(top, window.innerHeight - cardHeight - margin)),
      });
    };
    const frame = window.requestAnimationFrame(position);
    window.addEventListener("resize", position);
    document.addEventListener("scroll", position, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", position);
      document.removeEventListener("scroll", position, true);
      target?.classList.remove("powermail-tour-target");
    };
  }, [tourStep]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [history, busy]);

  const sendToBia = async (event?: SubmitEvent) => {
    event?.preventDefault();
    const prompt = input.trim();
    if (!prompt || busy) return;
    const userItem: AiHistoryItem = { role: "user", text: prompt };
    const nextHistory: AiHistoryItem[] = [...history, userItem].slice(-10);
    setHistory(nextHistory); setInput(""); setBusy(true); setError(null);
    try {
      const response = await apiRequest<AiGenerateResponse>("/api/v1/ai/generate", {
        method: "POST",
        body: JSON.stringify({
          mode: "assistant",
          prompt,
          history: nextHistory.slice(0, -1),
          ...(selectedMessage ? { messageContext: `${selectedMessage.subject}\n\n${selectedMessage.body.join("\n\n")}` } : {}),
        }),
      });
      setHistory((current) => [...current, { role: "model", text: response.text } as AiHistoryItem].slice(-12));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "A Bia não conseguiu responder agora.");
    } finally { setBusy(false); }
  };

  return (
    <>
      {introOpen ? (
        <div className="welcome-tour" role="dialog" aria-modal="true" aria-labelledby="welcome-intro-title">
          <div className="welcome-tour__veil" />
          <section className="welcome-tour__card welcome-tour__card--intro">
            <img className="welcome-tour__portrait" src="/brand/bia-avatar.png" alt="Bia, assistente do PowerMail" />
            <span className="welcome-tour__eyebrow">BEM-VINDO AO POWERMAIL</span>
            <h2 id="welcome-intro-title">Oi, sou a Bia.</h2>
            <p>Vou te guiar pelo PowerMail e mostrar como escrever, encontrar e organizar seus e-mails com mais velocidade.</p>
            <footer><button type="button" onClick={finishTour}>Agora não</button><button className="button button--primary" type="button" onClick={() => { setIntroOpen(false); setTourStep(0); }}>Vamos começar</button></footer>
          </section>
        </div>
      ) : null}
      {tourStep !== null ? (
        <div className="welcome-tour" role="dialog" aria-modal="true" aria-labelledby="welcome-tour-title">
          <div className="welcome-tour__veil" />
          <section className="welcome-tour__card" ref={tourCardRef} style={{ left: `${tourPosition.left}px`, top: `${tourPosition.top}px` }}>
            <header><img src="/brand/bia-avatar.png" alt="Bia" /><span><strong>Bia</strong><small>Assistente do PowerMail</small></span><button type="button" onClick={finishTour} aria-label="Fechar boas-vindas"><IconX size={17} /></button></header>
            <span className="welcome-tour__eyebrow">PASSO {tourStep + 1} DE {welcomeSteps.length}</span>
            <h2 id="welcome-tour-title">{welcomeSteps[tourStep].title}</h2>
            <p>{welcomeSteps[tourStep].description}</p>
            <footer><button type="button" onClick={finishTour}>Pular</button><span>{welcomeSteps.map((_, index) => <i className={index === tourStep ? "is-active" : ""} key={index} />)}</span>{tourStep > 0 ? <button type="button" onClick={() => setTourStep(tourStep - 1)}>Voltar</button> : null}<button className="button button--primary" type="button" onClick={() => tourStep === welcomeSteps.length - 1 ? finishTour() : setTourStep(tourStep + 1)}>{tourStep === welcomeSteps.length - 1 ? "Começar" : "Próximo"}</button></footer>
          </section>
        </div>
      ) : null}

      <aside className={`bia-assistant${open ? " is-open" : ""}`} aria-label="Bia, assistente do PowerMail">
        {nudge && !open ? <div className="bia-nudge"><button type="button" onClick={() => setNudge(false)} aria-label="Fechar convite"><IconX size={14} /></button><button type="button" onClick={() => { setOpen(true); setNudge(false); }}><strong>Oi! Sou a Bia.</strong><span>Posso escrever um e-mail ou te ajudar por aqui.</span></button></div> : null}
        {showNotificationNudge ? <div className="bia-nudge bia-nudge--notification"><button type="button" onClick={() => setNotificationNudgeDismissed(true)} aria-label="Fechar lembrete"><IconX size={14} /></button><button type="button" onClick={onEnableNotifications}><strong>{notificationPermission === "denied" || notificationStatus === "manual" ? "Notificações bloqueadas" : "Quer receber meus avisos?"}</strong><span>{notificationPermission === "denied" || notificationStatus === "manual" ? "Libere as notificações nas permissões do navegador para eu avisar sobre novos e-mails." : "Ative as notificações e eu aviso assim que um novo e-mail aparecer."}</span><em>{notificationPermission === "denied" || notificationStatus === "manual" ? "Ver como ativar" : "Ativar notificações"}</em></button></div> : null}
        {open ? (
          <section className="bia-panel" role="dialog" aria-labelledby="bia-title">
            <header><img src="/brand/bia-avatar.png" alt="" /><div><h2 id="bia-title">Bia</h2><span><i /> Assistente do PowerMail</span></div><button type="button" onClick={() => setOpen(false)} aria-label="Minimizar Bia"><IconX size={18} /></button></header>
            <div className="bia-panel__log" ref={logRef} role="log" aria-live="polite">
              {history.map((item, index) => <article className={item.role} key={`${item.role}-${index}`}><strong>{item.role === "model" ? "Bia" : "Você"}</strong><p>{item.text}</p></article>)}
              {busy ? <article className="model bia-typing"><strong>Bia</strong><p><i /><i /><i /></p></article> : null}
            </div>
            <div className="bia-panel__shortcuts"><button type="button" onClick={() => { onCompose(); setOpen(false); }}><IconPencil size={14} /> Escrever e-mail</button><button type="button" onClick={startTour}><IconSparkles size={14} /> Ver tour</button></div>
            <form onSubmit={(event) => void sendToBia(event)}><textarea rows={2} maxLength={8000} value={input} onInput={(event) => setInput(event.currentTarget.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.isComposing) { event.preventDefault(); void sendToBia(); } }} placeholder="Pergunte à Bia…" disabled={busy} /><button type="submit" aria-label="Enviar para Bia" disabled={busy || !input.trim()}><IconSend size={17} /></button>{error ? <p role="alert">{error}</p> : null}<small>IA pode cometer erros. Revise antes de enviar.</small></form>
          </section>
        ) : null}
        <button className="bia-launcher" type="button" onClick={() => { setOpen((current) => !current); setNudge(false); }} aria-label={open ? "Minimizar Bia" : "Conversar com a Bia"} aria-expanded={open}><img src="/brand/bia-avatar.png" alt="" /><span aria-hidden="true" /></button>
      </aside>
    </>
  );
}

function MobileNavigation({ view, onCompose, onViewChange }: { view: MainView; onCompose: () => void; onViewChange: (view: MainView) => void }) {
  return (
    <nav className="mobile-nav" aria-label="Navegação móvel">
      <button className={view === "mail" ? "is-active" : ""} type="button" onClick={() => onViewChange("mail")}>
        <IconInbox size={21} aria-hidden="true" />
        Entrada
      </button>
      <button className="mobile-nav__compose" type="button" onClick={onCompose}>
        <IconPencil size={21} aria-hidden="true" />
        Escrever
      </button>
      <button className={view === "board" ? "is-active" : ""} type="button" onClick={() => onViewChange("board")}>
        <IconLayoutKanban size={21} aria-hidden="true" />
        Quadro
      </button>
    </nav>
  );
}

export function App() {
  const session = useSession();
  const inbox = useInbox(session.status === "authenticated", session.data?.tenant.id, session.data?.activeMailboxId);
  const { messages, status, syncing, error, hiddenRecipientEmails, syncedAt, retry, refresh: refreshInbox, patchMessage, reorderBoard, hideRecipientSuggestion, send } = inbox;
  const online = useOnlineStatus();
  const mailNotifications = useMailNotifications(
    messages,
    session.data?.tenant.id,
    session.data?.activeMailboxId,
    status === "ready",
    session.data?.account.id,
  );
  const [initialLocation] = useState(appLocationFromUrl);
  const [notificationTarget, setNotificationTarget] = useState(notificationTargetFromUrl);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [mainView, setMainView] = useState<MainView>(initialLocation.mainView);
  const [settingsRoute, setSettingsRoute] = useState<SettingsRoute>(initialLocation.settingsRoute);
  const [activeFolder, setActiveFolder] = useState<FolderFilter>(initialLocation.activeFolder);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fullMessageId, setFullMessageId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchPageQuery, setSearchPageQuery] = useState(initialLocation.searchQuery);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeReplyTo, setComposeReplyTo] = useState<InboxMessage | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [notificationModalDismissedFor, setNotificationModalDismissedFor] = useState<string | null>(null);
  const [minimumSplashElapsed, setMinimumSplashElapsed] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setMinimumSplashElapsed(true), 900);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { window.localStorage.setItem("powermail-theme", theme); } catch { /* storage may be unavailable */ }
  }, [theme]);

  useEffect(() => {
    if (normalizePathname(window.location.pathname) === "/") {
      window.history.replaceState({}, "", folderPaths.all);
    }

    const handlePopState = () => {
      const location = appLocationFromUrl();
      setNotificationTarget(notificationTargetFromUrl());
      setMainView(location.mainView);
      setSettingsRoute(location.settingsRoute);
      if (location.mainView === "mail") setActiveFolder(location.activeFolder);
      if (location.mainView === "search") setSearchPageQuery(location.searchQuery);
      setSelectedId(null);
      setFullMessageId(null);
      setSearch("");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    setSelectedId(null);
    setFullMessageId(null);
  }, [session.data?.account.id, session.data?.activeMailboxId, session.data?.tenant.id]);

  useEffect(() => {
    if (session.status === "authenticated" && syncedAt) session.refresh();
  }, [session.refresh, session.status, syncedAt]);

  useEffect(() => {
    if (!toast) return;
    const timeoutId = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  const visibleMessages = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");
    return messages.filter((message) => {
      const matchesFolder =
        (activeFolder === "all" && message.direction === "inbound" && message.folder === "inbox") ||
        (activeFolder === "unread" && message.direction === "inbound" && message.folder === "inbox" && message.unread) ||
        (activeFolder === "starred" && message.starred && message.folder !== "trash") ||
        (activeFolder === "sent" && message.direction === "outbound" && message.deliveryStatus !== "draft" && message.folder !== "trash") ||
        (activeFolder === "drafts" && message.deliveryStatus === "draft" && message.folder !== "trash") ||
        (activeFolder === "archive" && message.folder === "archive") ||
        (activeFolder === "trash" && message.folder === "trash");
      const matchesSearch =
        !normalizedSearch ||
        `${message.sender.name} ${message.sender.email} ${message.fromAddress} ${message.toAddress} ${message.subject} ${message.preview}`
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch);
      return matchesFolder && matchesSearch;
    });
  }, [activeFolder, messages, search]);

  const selectedMessage = visibleMessages.find((message) => message.id === selectedId) || null;
  const fullMessage = messages.find((message) => message.id === fullMessageId) || null;
  const unreadCount = messages.filter(
    (message) => message.direction === "inbound" && message.folder === "inbox" && message.unread,
  ).length;
  const boardMessages = messages
    .filter((message) => message.direction === "inbound" && message.folder === "inbox")
    .filter((message, index, candidates) => candidates.findIndex((candidate) => candidate.threadId === message.threadId) === index);
  const draftCount = messages.filter((message) => message.deliveryStatus === "draft" && message.folder !== "trash").length;

  const pushAppPath = (path: string) => {
    if (`${window.location.pathname}${window.location.search}` !== path) {
      window.history.pushState({}, "", path);
    }
  };

  const navigateView = (view: MainView) => {
    const path = view === "mail"
      ? folderPaths[activeFolder]
      : view === "board"
        ? "/quadro"
        : view === "search"
          ? searchPath(searchPageQuery)
          : settingsPath(settingsRoute);
    pushAppPath(path);
    setMainView(view);
    setSelectedId(null);
    setFullMessageId(null);
  };

  const navigateFolder = (folder: FolderFilter) => {
    pushAppPath(folderPaths[folder]);
    setActiveFolder(folder);
    setMainView("mail");
    setSelectedId(null);
    setFullMessageId(null);
    setSearch("");
  };

  const navigateSearch = (query: string) => {
    pushAppPath(searchPath(query));
    setSearchPageQuery(query);
    setMainView("search");
    setSelectedId(null);
    setFullMessageId(null);
  };

  const navigateSettings = (route: SettingsRoute) => {
    const path = settingsPath(route);
    pushAppPath(path);
    setSettingsRoute(route);
    setMainView("settings");
    setSelectedId(null);
    setFullMessageId(null);
  };

  const handleMailboxChange = async (mailboxId: string) => {
    if (mailboxId === session.data?.activeMailboxId) return;
    navigateFolder("all");
    try {
      await session.switchMailbox(mailboxId);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Não foi possível trocar de caixa de e-mail.";
      setToast(message);
    }
  };

  const applyMessagePatch = async (
    id: string,
    patch: Parameters<typeof patchMessage>[1],
    successMessage?: string,
  ) => {
    try {
      await patchMessage(id, patch);
      if (successMessage) setToast(successMessage);
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Não foi possível atualizar a mensagem.");
    }
  };

  const applyBoardMessagePatch = async (
    id: string,
    patch: Parameters<typeof patchMessage>[1],
    successMessage?: string,
  ) => {
    await patchMessage(id, patch);
    if (successMessage) setToast(successMessage);
  };

  const openMessage = (id: string) => {
    setSelectedId(id);
    setWorkflowOpen(true);
    const message = messages.find((candidate) => candidate.id === id);
    if (message?.unread) void applyMessagePatch(id, { unread: false });
  };

  const openFullMessage = (id: string) => {
    const message = messages.find((candidate) => candidate.id === id);
    if (!message) return;
    setFullMessageId(id);
    if (message.unread) void applyMessagePatch(id, { unread: false });
  };

  const navigateToMessage = (id: string, candidates: InboxMessage[] = messages) => {
    const message = candidates.find((candidate) => candidate.id === id);
    if (!message) return;
    const folder: FolderFilter = message.folder === "archive"
      ? "archive"
      : message.folder === "trash"
        ? "trash"
        : message.deliveryStatus === "draft"
          ? "drafts"
          : message.direction === "outbound"
            ? "sent"
            : "all";
    navigateFolder(folder);
    setSelectedId(id);
    setWorkflowOpen(true);
    if (message.unread) void applyMessagePatch(id, { unread: false });
  };

  useEffect(() => {
    if (!notificationTarget || session.status !== "authenticated" || !session.data) return;
    let cancelled = false;

    const clearNotificationTarget = () => {
      const url = new URL(window.location.href);
      notificationTargetKeys.forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      setNotificationTarget(null);
    };

    const routeNotification = async () => {
      const targetWorkspace = session.data!.workspaces.find((workspace) => workspace.id === notificationTarget.tenantId);
      const targetMailbox = session.data!.mailboxes.find(
        (mailbox) => mailbox.id === notificationTarget.mailboxId && mailbox.tenantId === notificationTarget.tenantId,
      );
      if (!targetWorkspace || !targetMailbox) {
        if (!cancelled) {
          setToast("Esta notificação pertence a uma caixa que não está mais disponível para sua conta.");
          clearNotificationTarget();
        }
        return;
      }

      try {
        if (session.data!.tenant.id !== notificationTarget.tenantId) {
          await session.switchTenant(notificationTarget.tenantId);
          return;
        }
        if (session.data!.activeMailboxId !== notificationTarget.mailboxId) {
          await session.switchMailbox(notificationTarget.mailboxId);
          return;
        }

        let refreshedMessages = await refreshInbox();
        for (let attempt = 0; attempt < 2 && !refreshedMessages?.some((message) => message.id === notificationTarget.messageId); attempt += 1) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 450 * (attempt + 1)));
          if (cancelled) return;
          refreshedMessages = await refreshInbox();
        }
        if (cancelled) return;
        if (!refreshedMessages?.some((message) => message.id === notificationTarget.messageId)) {
          setToast("O e-mail da notificação ainda não apareceu na caixa. Atualize novamente em alguns segundos.");
          clearNotificationTarget();
          return;
        }
        navigateToMessage(notificationTarget.messageId, refreshedMessages);
        clearNotificationTarget();
      } catch (caught) {
        if (cancelled) return;
        setToast(caught instanceof Error ? caught.message : "Não foi possível abrir o e-mail desta notificação.");
        clearNotificationTarget();
      }
    };

    void routeNotification();
    return () => { cancelled = true; };
  }, [
    notificationTarget,
    refreshInbox,
    session.data?.activeMailboxId,
    session.data?.tenant.id,
    session.status,
  ]);

  const openComposer = (replyTo: InboxMessage | null = null) => {
    if (!session.data?.mailboxes.length) {
      setToast("Cadastre uma caixa de e-mail antes de enviar.");
      return;
    }
    setComposeReplyTo(replyTo);
    setComposeOpen(true);
  };

  const closeComposer = () => {
    setComposeOpen(false);
    setComposeReplyTo(null);
  };

  const enableNotifications = async () => {
    const result = await mailNotifications.activate();
    if (result === "enabled") {
      setToast("Notificações ativadas. A Bia vai avisar mesmo com o PowerMail fechado.");
    } else if (result === "unsupported") {
      setToast("Este navegador não oferece notificações em segundo plano para o PowerMail.");
    }
    return result;
  };

  const handleSend = async (input: SendMessageInput) => {
    const response = await send(input);
    setToast(response.delivery === "queued"
      ? `Mensagem protegida na fila SMTP. Restam ${response.quota.remaining} destinatários nesta hora.`
      : `Mensagem enviada. Restam ${response.quota.remaining} destinatários nesta hora.`);
  };

  if (!minimumSplashElapsed || session.status === "loading") {
    return (
      <main className="splash-screen" aria-busy="true" aria-label="Carregando o PowerMail">
        <div className="splash-brand">
          <img src="/brand/simbolo-powermail.jpeg" alt="" aria-hidden="true" />
          <div>
            <strong>PowerMail</strong>
            <span>Preparando sua caixa de entrada</span>
          </div>
        </div>
      </main>
    );
  }

  if (session.status !== "authenticated" || !session.data) {
    return (
      <LoginScreen
        startupError={session.error}
        onAuthenticated={session.retry}
        onRetrySession={session.status === "error" ? session.retry : undefined}
      />
    );
  }

  const notificationModalOpen = notificationModalDismissedFor !== session.data.account.id
    && (
      mailNotifications.backgroundStatus === "idle"
      || mailNotifications.backgroundStatus === "subscribing"
      || mailNotifications.backgroundStatus === "manual"
      || mailNotifications.backgroundStatus === "error"
    );

  return (
    <div className={`app-shell${selectedMessage ? " has-mobile-reader" : ""}${sidebarCollapsed ? " is-sidebar-collapsed" : ""}${mainView === "settings" ? " is-settings-view" : ""}`}>
      <Sidebar
        activeFolder={activeFolder}
        collapsed={sidebarCollapsed}
        draftCount={draftCount}
        mainView={mainView}
        session={session.data}
        unreadCount={unreadCount}
        onCompose={() => openComposer()}
        onFolderChange={navigateFolder}
        onMailboxChange={handleMailboxChange}
        onRefreshMailboxes={session.refresh}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        onViewChange={navigateView}
      />

      <main className="app-main">
        <Topbar
          messages={messages}
          online={online}
          session={session.data}
          syncing={syncing}
          theme={theme}
          onLogout={() => {
            void session.logout().catch((caught: unknown) => {
              setToast(caught instanceof Error ? caught.message : "Não foi possível sair.");
            });
          }}
          onMailboxChange={(mailboxId) => void handleMailboxChange(mailboxId)}
          onOpenMessage={navigateToMessage}
          onOpenSettings={() => navigateSettings("overview")}
          onRefreshMailboxes={session.refresh}
          onRestartWelcome={() => window.dispatchEvent(new Event("powermail:restart-welcome"))}
          onShowAllResults={(query) => {
            navigateSearch(query);
          }}
          onThemeChange={() => setTheme((current) => (current === "light" ? "dark" : "light"))}
        />
        {mainView === "mail" ? (
          <div className={`mail-workspace${!selectedMessage || !workflowOpen ? " is-workflow-closed" : ""}`}>
            <MessageList
              activeFolder={activeFolder}
              messages={visibleMessages}
              selectedId={selectedId}
              status={status}
              error={error}
              syncedAt={syncedAt}
              onOpen={openMessage}
              onRetry={retry}
              onSearch={setSearch}
              onToggleStar={(id) => {
                const message = messages.find((candidate) => candidate.id === id);
                if (message) void applyMessagePatch(id, { starred: !message.starred });
              }}
            />
            <MessageReader
              message={selectedMessage}
              onArchive={() => {
                if (!selectedMessage) return;
                const unarchive = selectedMessage.folder === "archive";
                void applyMessagePatch(selectedMessage.id, { folder: unarchive ? "inbox" : "archive" }, unarchive ? "Conversa movida para a entrada." : "Conversa arquivada.");
                setSelectedId(null);
              }}
              onBack={() => setSelectedId(null)}
              onCompose={() => openComposer(selectedMessage)}
              onOpenFull={() => selectedMessage && openFullMessage(selectedMessage.id)}
              onToggleWorkflow={() => setWorkflowOpen((current) => !current)}
              onToggleRead={() => selectedMessage && void applyMessagePatch(selectedMessage.id, { unread: !selectedMessage.unread }, selectedMessage.unread ? "Mensagem marcada como lida." : "Mensagem marcada como não lida.")}
              onToggleStar={() => selectedMessage && void applyMessagePatch(selectedMessage.id, { starred: !selectedMessage.starred }, selectedMessage.starred ? "Removida dos importantes." : "Marcada como importante.")}
              onTrash={() => {
                if (!selectedMessage) return;
                void applyMessagePatch(selectedMessage.id, { folder: "trash" }, "Conversa movida para a lixeira.");
                setSelectedId(null);
              }}
              workflowOpen={workflowOpen}
            />
            {selectedMessage && workflowOpen ? (
              <WorkflowPanel
                message={selectedMessage}
                onClose={() => setWorkflowOpen(false)}
                onPatchMessage={(patch, successMessage) => selectedMessage
                  ? applyBoardMessagePatch(selectedMessage.id, patch, successMessage)
                  : Promise.resolve()}
              />
            ) : null}
          </div>
        ) : mainView === "board" ? (
          <KanbanBoard
            messages={boardMessages}
            tenantId={session.data.tenant.id}
            onOpenMessage={openFullMessage}
            onPatchMessage={applyBoardMessagePatch}
            onReorder={async (input) => { await reorderBoard(input); }}
            onToast={setToast}
          />
        ) : mainView === "search" ? (
          <SearchPage messages={messages} initialQuery={searchPageQuery} onOpenMessage={navigateToMessage} />
        ) : (
          <SettingsPage
            route={settingsRoute}
            session={session.data}
            theme={theme}
            notifications={{
              permission: mailNotifications.permission,
              status: mailNotifications.backgroundStatus,
              loading: mailNotifications.preferencesLoading,
              selectedMailboxIds: mailNotifications.selectedMailboxIds,
              onActivate: enableNotifications,
              onSave: async (mailboxIds) => { await mailNotifications.saveMailboxPreferences(mailboxIds); },
            }}
            onBack={() => navigateView("mail")}
            onRouteChange={navigateSettings}
            onSessionRefresh={session.refresh}
            onThemeChange={setTheme}
            onToast={setToast}
          />
        )}
      </main>

      <MobileNavigation view={mainView} onCompose={() => openComposer()} onViewChange={navigateView} />
      <ComposeDialog
        mailboxes={session.data.mailboxes.filter((mailbox) => mailbox.id === session.data!.activeMailboxId)}
        messages={messages}
        hiddenRecipientEmails={hiddenRecipientEmails}
        open={composeOpen}
        replyTo={composeReplyTo}
        onClose={closeComposer}
        onHideRecipientSuggestion={hideRecipientSuggestion}
        onSend={handleSend}
      />
      <MessageFullDialog
        message={fullMessage}
        onArchive={() => {
          if (!fullMessage) return;
          const unarchive = fullMessage.folder === "archive";
          void applyMessagePatch(fullMessage.id, { folder: unarchive ? "inbox" : "archive" }, unarchive ? "Conversa movida para a entrada." : "Conversa arquivada.");
          setFullMessageId(null);
        }}
        onClose={() => setFullMessageId(null)}
        onCompose={() => {
          if (!fullMessage) return;
          const replyTo = fullMessage;
          setFullMessageId(null);
          openComposer(replyTo);
        }}
        onPatchMessage={(patch, successMessage) => fullMessage
          ? applyBoardMessagePatch(fullMessage.id, patch, successMessage)
          : Promise.resolve()}
        onToggleRead={() => fullMessage && void applyMessagePatch(fullMessage.id, { unread: !fullMessage.unread }, fullMessage.unread ? "Mensagem marcada como lida." : "Mensagem marcada como não lida.")}
        onToggleStar={() => fullMessage && void applyMessagePatch(fullMessage.id, { starred: !fullMessage.starred }, fullMessage.starred ? "Removida dos importantes." : "Marcada como importante.")}
        onTrash={() => {
          if (!fullMessage) return;
          void applyMessagePatch(fullMessage.id, { folder: "trash" }, "Conversa movida para a lixeira.");
          setFullMessageId(null);
        }}
      />
      {toast ? (
        <div className="app-toast" role="status">
          <IconCheck size={18} aria-hidden="true" />
          {toast}
        </div>
      ) : null}
      <NotificationSetupModal
        isEdge={mailNotifications.isEdge}
        open={notificationModalOpen}
        permission={mailNotifications.permission}
        status={mailNotifications.backgroundStatus}
        onActivate={enableNotifications}
        onClose={() => setNotificationModalDismissedFor(session.data!.account.id)}
      />
      <BiaAssistant
        notificationSetupOpen={notificationModalOpen}
        notificationStatus={mailNotifications.backgroundStatus}
        notificationPermission={mailNotifications.permission}
        onEnableNotifications={() => setNotificationModalDismissedFor(null)}
        session={session.data}
        selectedMessage={selectedMessage}
        onCompose={() => openComposer()}
      />
      <PwaPrompt />
    </div>
  );
}
